
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import CopyButton from "./CopyChat";
import { formatCost } from "./costFormat";
import Chart from "../charts/Chart";
import axios from "axios";
import { useAgentConfig } from "../contexts/AgentConfigContext";
import { useAuth } from "../contexts/AuthContext";
import { useDarkMode } from "../contexts/DarkModeContext";
import SourceCard from "./SourceCard";
import DocxPreviewModal from "./DocxPreviewModal";
import HighlightPreviewModal from "./HighlightPreviewModal";
import VideoPreviewModal from "./VideoPreviewModal";
import FilePreviewModal from "./FilePreviewModal";
import { urlFileExt } from "./previewUtils";
import PdfPreviewModal from "./PdfPreviewModal";
import CodeBlock from "../common_jsx/CodeBlock";
import Tooltip from "../common_jsx/Tooltip";
import { useToast } from "../components/Toast";
import Options from "./Options";
import ClarificationOptions from "./ClarificationOptions";
import ActivityTimeline from "./ActivityTimeline";
import { parseJSONResponse, isJSONResponse } from "../utils/responseParser";
import { MentionText } from "../utils/mentions";
import {
  hasExportableText,
  isRcaAnswer,
  buildMarkdown,
  downloadTextFile,
  exportFilename,
} from "../utils/answerExport";
import { getTabConfig } from "../utils/tabConfig";
import { ThumbsUp, ThumbsDown, DollarSign, X, ChevronDown, ChevronUp, MessageSquare, Hash, HardDrive, Database, Globe, HelpCircle, Table, Code2, Ticket } from 'lucide-react';
import { parseMessageToArray } from "../utils/tableParser";
import { resultSet } from "../utils/resultSet";
import { sanitizeHTML } from "../utils/sanitize";
import { looksLikeHTML } from "../utils/html";
import useSmoothReveal from "../utils/useSmoothReveal";
import { animTrace, animTraceEnvironment } from "../utils/animDebug";
import Spinner from "../utils/icons/Spinner";
import { fenceLanguage } from "./codeFence";



/**
 * Helper function to parse message to array for Data tab display
 * Handles markdown tables, JSON strings, and arrays
 * @param {any} message - The message to parse
 * @param {number|null} limit - Max rows to return (null for all rows)
 */
// The API mixes second- and millisecond-epoch timestamps across sources;
// anything below this magnitude as milliseconds would predate the year 2001,
// so it's treated as seconds instead. Shared so every consumer of a raw
// `timestamp` value agrees on what it means.
const toMessageDate = (timestamp) => {
  const ts = Number(timestamp);
  return ts > 10000000000 ? new Date(ts) : new Date(ts * 1000);
};

// Google Chat-style smart timestamp formatting
const formatSmartTimestamp = (timestamp) => {
  if (!timestamp) return "";

  const ts = Number(timestamp);
  if (isNaN(ts)) return "";

  const messageDate = toMessageDate(timestamp);
  const now = new Date();
  
  // Helper to get start of day
  const getStartOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };
  
  const todayStart = getStartOfDay(now);
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const sevenDaysAgo = new Date(todayStart);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  const diffMs = now - messageDate;
  let diffMins = Math.floor(diffMs / 60000);
  // Clamp diffMins to a minimum of 0 to avoid negative values (future timestamps)
  if (diffMins < 0) diffMins = 0;
  
  
  // Today - within last 1 hour: relative time
  if (messageDate >= todayStart && diffMins < 60) {
    if (diffMins < 1) return "just now";
    return `${diffMins} min ago`;
  }
  
  // Today - older than 1 hour: time only
  if (messageDate >= todayStart) {
    return messageDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  }
  
  // Yesterday
  if (messageDate >= yesterdayStart && messageDate < todayStart) {
    const time = messageDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
    return `Yesterday, ${time}`;
  }
  
  // Within last 7 days: day name + time
  if (messageDate >= sevenDaysAgo) {
    const dayName = messageDate.toLocaleDateString('en-US', { weekday: 'short' });
    const time = messageDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
    return `${dayName}, ${time}`;
  }
  
  // Older than 7 days: date + time
  const date = messageDate.toLocaleDateString('en-US', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
  const time = messageDate.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit', 
    hour12: true 
  });
  return `${date}, ${time}`;
};

// Helper component for user avatar with error handling
const UserAvatar = ({ photoURL, userName }) => {
  const [imageError, setImageError] = useState(false);

  // Get first letter of name for fallback
  const getInitial = (name) => {
    if (!name) return 'U';
    return name.charAt(0).toUpperCase();
  };

  // Reset error state when photoURL changes
  useEffect(() => {
    setImageError(false);
  }, [photoURL]);

  if (photoURL && !imageError) {
    return (
      <img
        src={photoURL}
        alt={userName || 'User'}
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
        className="w-9 h-9 rounded-full object-cover border-2 border-line"
        onError={() => {
          console.error('Failed to load avatar image:', photoURL);
          setImageError(true);
        }}
      />
    );
  }

  return (
    <div className="w-9 h-9 rounded-full flex items-center justify-center text-brand-fg font-semibold text-sm bg-gradient-to-br from-blue-500 to-purple-600 border-2 border-line shadow-md">
      {getInitial(userName)}
    </div>
  );
};

UserAvatar.propTypes = {
  photoURL: PropTypes.string,
  userName: PropTypes.string,
};

// Dynamic timestamp component that updates automatically
const DynamicTimestamp = ({ timestamp, isUserMessage }) => {
  const [formattedTime, setFormattedTime] = useState("");

  useEffect(() => {
    // If there is no timestamp, do not attempt to format or schedule updates
    if (!timestamp) {
      setFormattedTime("");
      return;
    }

    // Initial format
    setFormattedTime(formatSmartTimestamp(timestamp));

    // Only schedule updates if the timestamp is within the last hour (relative time)
    const now = new Date();
    const ts = Number(timestamp);
    const messageDate = ts > 10000000000 ? new Date(ts) : new Date(ts * 1000);
    const diffMins = Math.floor((now - messageDate) / 60000);

    let interval = null;
    if (diffMins < 60 && diffMins >= 0) {
      interval = setInterval(() => {
        setFormattedTime(formatSmartTimestamp(timestamp));
      }, 60000); // Update every 60 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timestamp]);

  if (!formattedTime) return null;

  return (
    <div className={`mt-1 text-xs text-fg-muted ${isUserMessage ? 'text-right' : ''}`}>
      {formattedTime}
    </div>
  );
};

DynamicTimestamp.propTypes = {
  timestamp: PropTypes.number,
  isUserMessage: PropTypes.bool,
};

// Code component for rendering code blocks
const CodeComponent = ({ node, inline, className, children, ...props }) => {
  const language = fenceLanguage(className);

  let childrenString = "";
  if (Array.isArray(children)) {
    childrenString = children.join("");
  } else if (children != null) {
    childrenString = String(children);
  }

  return !inline && language ? (
    <CodeBlock language={language} code={childrenString.replace(/\n$/, "")} {...props} />
  ) : (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

CodeComponent.propTypes = {
  node: PropTypes.object,
  inline: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.node]),
};

// Table component for rendering tables
const TableComponent = ({ node, className, children, ...props }) => {
  return (
    <div className="overflow-x-auto table-scrollbar">
      <table
        className="min-w-full divide-y divide-line"
        {...props}
      >
        {children}
      </table>
    </div>
  );
};

TableComponent.propTypes = {
  node: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node,
};

// Thead component for table headers
const TheadComponent = ({ children, ...props }) => {
  return (
    <thead className="bg-surface-raised" {...props}>
      {children}
    </thead>
  );
};

TheadComponent.propTypes = {
  children: PropTypes.node,
};

// Tbody component for table body
const TbodyComponent = ({ children, ...props }) => {
  return (
    <tbody className="divide-y divide-line bg-surface" {...props}>
      {children}
    </tbody>
  );
};

TbodyComponent.propTypes = {
  children: PropTypes.node,
};

// Tr component for table rows
const TrComponent = ({ children, ...props }) => {
  return (
    <tr className="hover:bg-surface-raised" {...props}>
      {children}
    </tr>
  );
};

TrComponent.propTypes = {
  children: PropTypes.node,
};

// Th component for table headers
const ThComponent = ({ children, ...props }) => {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-fg" {...props}>
      {children}
    </th>
  );
};

ThComponent.propTypes = {
  children: PropTypes.node,
};

// Td component for table cells
const TdComponent = ({ children, ...props }) => {
  return (
    <td className="px-4 py-3 text-sm whitespace-nowrap text-fg" {...props}>
      {children}
    </td>
  );
};

TdComponent.propTypes = {
  children: PropTypes.node,
};

// Helper component for rendering markdown with custom components
// Allow only safe URI schemes in markdown links to prevent javascript: XSS
const SAFE_HREF_RE = /^(https?:|mailto:|tel:|\/|#)/i;
const sanitizeHref = (href) => (href && SAFE_HREF_RE.test(href) ? href : '#');

// Logs a warning the first time a given key is seen, then stays silent —
// keeps the console readable when a render path keeps re-triggering.
const _warnOnceKeys = new Set();
const logging_warn_once = (key, ...args) => {
  if (_warnOnceKeys.has(key)) return;
  _warnOnceKeys.add(key);
  // eslint-disable-next-line no-console
  console.warn(key, ...args);
};

// Tracks which citation chunk is currently being opened so its pill can show a
// spinner and ignore further clicks until the preview resolves. Provided by the
// message component and consumed by the citation pills in MarkdownRenderer.
const CitationLoadingContext = React.createContext(null);

// Provides web_sources (Gemini google_search grounding) to the markdown
// renderer so inline `#gs-1,2,3` markers can be expanded into rich source
// pills (favicon + site + overflow).
const WebSourcesContext = React.createContext(null);

// Friendly site label for common hosts; falls back to the bare hostname.
const SITE_LABELS = {
  'reddit.com': 'Reddit',
  'youtube.com': 'YouTube',
  'youtu.be': 'YouTube',
  'twitter.com': 'X',
  'x.com': 'X',
  'github.com': 'GitHub',
  'stackoverflow.com': 'Stack Overflow',
  'wikipedia.org': 'Wikipedia',
  'medium.com': 'Medium',
  'linkedin.com': 'LinkedIn',
};

const getHost = (uri) => {
  try { return new URL(uri).hostname.replace(/^www\./, ''); } catch { return ''; }
};

// Gemini grounding URIs are vertexaisearch.cloud.google.com redirect links —
// useless for display. We hide that host and rely on the title/domain instead.
const VERTEX_REDIRECT_HOST = 'vertexaisearch.cloud.google.com';

// Best display host for a source: prefer the explicit `domain` Google sometimes
// returns; otherwise try to recover a domain from the trailing "- site.com" in
// the title; otherwise parse the URI but discard the Vertex redirect host.
// Returns '' when no real host is recoverable.
const sourceHost = (source) => {
  if (source.domain) return source.domain.replace(/^www\./, '');
  const titleTail = source.title?.match(/[-–—]\s*([a-z0-9.-]+\.[a-z]{2,})\s*$/i);
  if (titleTail) return titleTail[1].toLowerCase();
  const host = getHost(source.uri);
  if (host && host !== VERTEX_REDIRECT_HOST) return host;
  return '';
};

// Host used to fetch the favicon — falls back to the URI's host (typically
// Google's, since the redirect goes through vertexaisearch.cloud.google.com),
// so the pill is never iconless.
const faviconHost = (source) => sourceHost(source) || getHost(source.uri);

const siteLabel = (source) => {
  const title = source.title?.trim();
  if (title) return title;
  const host = sourceHost(source);
  if (host) {
    const parts = host.split('.');
    const baseTwo = parts.slice(-2).join('.');
    return SITE_LABELS[host] || SITE_LABELS[baseTwo] || host;
  }
  return `Citation ${source.index}`;
};

// Hover popover that lists every grounding source for a pill, each individually
// clickable. Receives the parent's enter/leave handlers so moving the cursor
// from the pill onto the popover doesn't dismiss it.
const GroundingPopover = ({ sources, onMouseEnter, onMouseLeave }) => {
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="absolute z-50 left-0 top-full mt-1 w-72 rounded-lg border border-line shadow-lg p-1.5 bg-surface-raised text-fg"
    >
      <ul className="space-y-0.5 max-h-72 overflow-y-auto">
        {sources.map((s) => {
          const host = sourceHost(s);
          const iconHost = faviconHost(s);
          const favicon = iconHost
            ? `https://www.google.com/s2/favicons?sz=64&domain=${encodeURIComponent(iconHost)}`
            : null;
          const title = s.title?.trim() || siteLabel(s);
          return (
            <li key={`${s.index}-${s.uri}`}>
              <a
                href={s.uri}
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline flex items-start gap-2 px-2 py-1.5 rounded-md hover:bg-surface"
              >
                {favicon && (
                  <img
                    src={favicon}
                    alt=""
                    width={14}
                    height={14}
                    className="rounded-sm mt-0.5 flex-none"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium leading-snug line-clamp-2">
                    {title}
                  </div>
                  {host && (
                    <div className="text-[10px] truncate mt-0.5 text-fg-muted">
                      {host}
                    </div>
                  )}
                </div>
                <span className="text-[10px] font-semibold flex-none mt-0.5 text-fg-muted">
                  {s.index}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

GroundingPopover.propTypes = {
  sources: PropTypes.array.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

// Inline grounding-source pill (Perplexity-style):
//   [favicon] <source title> +N
const GroundingPill = ({ sources }) => {
  const [open, setOpen] = React.useState(false);
  const closeTimer = React.useRef(null);
  React.useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  if (!sources || sources.length === 0) return null;
  const primary = sources[0];
  const iconHost = faviconHost(primary);
  const favicon = iconHost
    ? `https://www.google.com/s2/favicons?sz=64&domain=${encodeURIComponent(iconHost)}`
    : null;
  const label = siteLabel(primary);
  const overflow = sources.length - 1;

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <span
      className="relative inline-block align-baseline mx-0.5"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <a
        href={primary.uri}
        target="_blank"
        rel="noopener noreferrer"
        className="no-underline inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-line text-[11px] leading-none transition-colors bg-surface-raised hover:bg-surface text-fg"
      >
        {favicon && (
          <img
            src={favicon}
            alt=""
            width={12}
            height={12}
            className="rounded-sm flex-none"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <span className="font-medium truncate max-w-[160px]">{label}</span>
        {overflow > 0 && (
          <span className="font-semibold text-fg-muted">+{overflow}</span>
        )}
      </a>
      {open && (
        <GroundingPopover
          sources={sources}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        />
      )}
    </span>
  );
};

GroundingPill.propTypes = {
  sources: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.number,
    uri: PropTypes.string,
    title: PropTypes.string,
    domain: PropTypes.string,
  })),
};

// Reads the `#gs-1,2,3` marker emitted by the Gemini adapter and resolves the
// indices against the web_sources list in WebSourcesContext.
const GroundingPillFromMarker = ({ href }) => {
  const webSources = React.useContext(WebSourcesContext);
  if (!webSources || webSources.length === 0) return null;
  const indices = href
    .slice(4)
    .split(',')
    .map((s) => parseInt(s, 10))
    .filter((n) => Number.isFinite(n));
  const byIndex = new Map(webSources.map((s) => [s.index, s]));
  const resolved = indices.map((i) => byIndex.get(i)).filter(Boolean);
  if (resolved.length === 0) return null;
  return <GroundingPill sources={resolved} />;
};

GroundingPillFromMarker.propTypes = {
  href: PropTypes.string.isRequired,
};

// Citation pill: while any citation is opening, the one being loaded shows a
// spinner and the rest are disabled, so a user can't fire off duplicate
// open-requests by clicking repeatedly before the preview modal appears.
const CitationPill = ({ chunkIdx, onChunkClick, pillClass, children }) => {
  const loadingChunkIdx = React.useContext(CitationLoadingContext);
  const isLoading = loadingChunkIdx === chunkIdx;
  const isBusy = loadingChunkIdx != null;
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); if (!isBusy) onChunkClick(chunkIdx); }}
      disabled={isBusy}
      title="View source"
      className={`${pillClass} transition-opacity ${isBusy ? 'cursor-wait opacity-60' : 'cursor-pointer hover:opacity-80'}`}
    >
      {isLoading ? <span className="inline-block w-2.5 h-2.5 border-2 border-current border-t-transparent rounded-full animate-spin" /> : children}
    </button>
  );
};

CitationPill.propTypes = {
  chunkIdx: PropTypes.number.isRequired,
  onChunkClick: PropTypes.func.isRequired,
  pillClass: PropTypes.string,
  children: PropTypes.node,
};

// Renders Google Search grounding output:
//   - A "Web sources" list keyed by the same [N] indices the Gemini adapter
//     inlined into the answer text as [N](uri) markdown links.
//   - The `search_entry_point.rendered_content` HTML chip, which Google's ToS
//     requires us to display verbatim when google_search grounding is used.
//     See https://ai.google.dev/gemini-api/docs/google-search
// The HTML chip ships its own <style> rules, so we inject it raw rather than
// running it through DOMPurify (which would strip the inline <style> block).
const WebGroundingPanel = ({ searchEntryPointHtml }) => {
  const hasChip = typeof searchEntryPointHtml === 'string' && searchEntryPointHtml.length > 0;
  if (!hasChip) return null;
  return (
    <div className="mt-3 pt-3 border-t border-line">
      <div
        // ToS-mandated Google Search Suggestions chip — render verbatim.
        dangerouslySetInnerHTML={{ __html: searchEntryPointHtml }}
      />
    </div>
  );
};

WebGroundingPanel.propTypes = {
  searchEntryPointHtml: PropTypes.string,
};

const MarkdownRenderer = ({ content, onChunkClick }) => {
  if (typeof content !== "string") {
    if (content == null || content === false) return null;
    if (React.isValidElement(content)) return content;
    // Plain JS object/array slipped through (e.g. an unparsed insights payload
    // like `{insight, data_source, source, suggested_questions, pagination}`).
    // Returning it directly throws "Objects are not valid as a React child"
    // and crashes the message tree — stringify it so the page survives.
    if (typeof content === "object") {
      logging_warn_once(
        "MarkdownRenderer received a non-element object as content; stringifying",
        content,
      );
      try {
        return (
          <pre className="whitespace-pre-wrap text-xs">
            {JSON.stringify(content, null, 2)}
          </pre>
        );
      } catch {
        return null;
      }
    }
    return String(content);
  }

  return (
    <div className="text-fg">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code: ({ node, inline, className, children, ...props }) => {
            if (inline) {
              // Normalize children for inline code to avoid rendering artifacts:
              // - If children is an array, join its elements as strings (ignoring null/undefined).
              // - If children is null or undefined, treat as an empty string.
              // This matches the logic in CodeComponent and prevents commas or other artifacts.
              let childrenString = '';
              if (Array.isArray(children)) {
                childrenString = children.filter(c => c != null).join('');
              } else if (children != null) {
                childrenString = String(children);
              }
              childrenString = childrenString.replace(/\n$/, '');
              return (
                <code
                  className="px-2 py-1 rounded-md font-mono text-xs font-medium bg-surface-raised text-danger border border-line"
                  {...props}
                >
                  {childrenString}
                </code>
              );
            }
            return <CodeComponent node={node} inline={inline} className={className} {...props}>{children}</CodeComponent>;
          },
          // The scroll box for a fenced block. tabIndex because a scrollable
          // region that is not focusable can only be reached with a pointer
          // (WCAG 2.1.1); custom-scrollbar so the bar is token-coloured rather
          // than the platform default sitting inside a dark block. `node` is
          // react-markdown's AST entry and is not a DOM attribute.
          pre: ({ node, children, ...props }) => (
            <pre className="custom-scrollbar" tabIndex={0} {...props}>
              {children}
            </pre>
          ),
          table: TableComponent,
          thead: TheadComponent,
          tbody: TbodyComponent,
          tr: TrComponent,
          th: ThComponent,
          td: TdComponent,
          strong: ({ children }) => (
            <strong className="font-semibold text-fg">
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em className="text-fg-secondary">
              {children}
            </em>
          ),
          h1: ({ children }) => (
            <h1 className="text-3xl font-bold mt-4 mb-3 text-fg">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-2xl font-bold mt-4 mb-2.5 text-fg">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-bold mt-3 first:mt-0 mb-3 tracking-tight text-fg">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-base font-semibold mt-3 mb-2 text-fg">
              {children}
            </h4>
          ),
          a: ({ children, href }) => {
            const pillClass = "inline-flex items-center justify-center align-super text-[10px] font-semibold leading-none rounded-full w-4 h-4 mx-0.5 bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300";
            if (href && href.startsWith('#cite-')) {
              const chunkIdx = parseInt(href.slice(6), 10);
              if (onChunkClick) {
                return <CitationPill chunkIdx={chunkIdx} onChunkClick={onChunkClick} pillClass={pillClass}>{children}</CitationPill>;
              }
              return <span className={pillClass}>{children}</span>;
            }
            // Gemini google_search grounding marker: `[source](#gs-1,2,3)` where
            // the indices are 1-based into reference.web_sources. Resolve them
            // and render a Perplexity-style pill (favicon + site + overflow).
            if (href && href.startsWith('#gs-')) {
              return <GroundingPillFromMarker href={href} />;
            }
            // Legacy fallback: older persisted messages were generated before the
            // backend switched to `#gs-` markers and contain `[N](https://...)`
            // where N is the citation number. Render those as a single-source
            // pill so old conversations don't regress to plain blue numbers.
            const childText = Array.isArray(children)
              ? children.filter((c) => typeof c === 'string').join('')
              : (typeof children === 'string' ? children : '');
            const numericText = childText.trim();
            if (href && /^https?:\/\//i.test(href) && /^\d+$/.test(numericText)) {
              return (
                <GroundingPill
                  sources={[{ index: parseInt(numericText, 10), uri: href, title: '' }]}
                />
              );
            }
            return (
              <a
                href={sanitizeHref(href)}
                className="font-medium hover:underline text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300"
                rel="noopener noreferrer"
                {...(href && href.startsWith('http') ? { target: '_blank' } : {})}
              >
                {children}
              </a>
            );
          },
          li: ({ children }) => (
            <li className="mb-2.5 leading-relaxed text-fg">
              {children}
            </li>
          ),
          p: ({ children }) => (
            <p className="mb-4 leading-relaxed text-fg">
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc pl-7 mb-5 mt-2 space-y-1 text-fg">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-7 mb-5 mt-2 space-y-1 text-fg">
              {children}
            </ol>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

MarkdownRenderer.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onChunkClick: PropTypes.func,
};

// Helper component for rendering table data
const TableRenderer = ({ message, id, setPaginationState }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10); // Removed unused setRowsPerPage
  const [tableData, setTableData] = useState({ headers: [], dataRows: [], totalRows: 0 });
  const [error, setError] = useState(false);

  // Parse table data
  useEffect(() => {
    try {
      const rows = message.trim().split("\n");
      const headers = rows[0]
        .split("|")
        .filter((cell) => cell.trim())
        .map((cell) => cell.trim());

      const dataRows = rows.slice(2).map((row) => {
        return row
          .split("|")
          .filter((cell) => cell.trim())
          .map((cell) => cell.trim());
      });

      setTableData({ headers, dataRows, totalRows: dataRows.length });
      setError(false);
    } catch (e) {
      console.error("Error parsing table:", e);
      setError(true);
    }
  }, [message]);

  const totalPages = Math.ceil(tableData.totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, tableData.totalRows);
  const currentRows = tableData.dataRows.slice(startIndex, endIndex);
  const showPagination = tableData.totalRows > 10;

  // Update pagination state for toolbar
  useEffect(() => {
    if (setPaginationState && showPagination) {
      setPaginationState({
        isLoading: false,
        pageNum: currentPage,
        totalPages,
        startRecord: startIndex + 1,
        endRecord: endIndex,
        totalRows: tableData.totalRows,
        handlePageChange: (newPage) => {
          if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
          }
        },
        showPagination: true,
      });
    } else if (setPaginationState) {
      setPaginationState({ showPagination: false });
    }
  }, [currentPage, totalPages, startIndex, endIndex, tableData.totalRows, showPagination, setPaginationState]);

  if (error) {
    return <MarkdownRenderer content={message} />;
  }

  return (
    <div className="w-full">
      {/* Table */}
      <div className="overflow-x-auto table-scrollbar">
        <table className="min-w-full divide-y divide-line">
          <thead className="bg-surface-raised">
            <tr>
              {tableData.headers.map((header, headerIndex) => (
                <th key={`header-${header}-${headerIndex}`} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-fg">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line bg-surface">
            {currentRows.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}-${row[0] || rowIndex}`} className="hover:bg-surface-raised">
                {row.map((cell, cellIndex) => (
                  <td key={`cell-${rowIndex}-${cellIndex}-${cell}`} className="px-4 py-3 text-sm whitespace-nowrap text-fg">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

TableRenderer.propTypes = {
  message: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setPaginationState: PropTypes.func,
};

// Helper component for rendering message content
const MessageContent = ({
  message,
  isTable,
  containsMarkdown,
  id,
  handleSend,
  isAuthorAdmin,
  clarificationOptions,
  handleClarificationOptionClick,
  clarificationOptionsDisabled,
  selectedClarificationOption,
  setPaginationState,
  onChunkClick,
}) => {
  if (message === false) {
    return <span className="text-fg">Apologies</span>;
  }

  const renderMessageContent = () => {
    // HTML string: render as sanitized HTML (e.g. key_insights pre-formatted as HTML)
    if (looksLikeHTML(message)) {
      return (
        <>
          <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(message) }} />
          {isAuthorAdmin && clarificationOptions && clarificationOptions.length > 0 && (
            <div className="mt-3">
              <ClarificationOptions
                options={clarificationOptions}
                onOptionClick={handleClarificationOptionClick}
                disabled={clarificationOptionsDisabled}
                selectedOption={selectedClarificationOption}
              />
            </div>
          )}
        </>
      );
    }

    // Check if message is a JSON response that needs parsing
    if (isJSONResponse(message)) {
      const parsedContent = parseJSONResponse(message);
      return (
        <>
          <MarkdownRenderer content={parsedContent} onChunkClick={onChunkClick} />
          {isAuthorAdmin && clarificationOptions && clarificationOptions.length > 0 && (
            <ClarificationOptions
              options={clarificationOptions}
              onOptionClick={handleClarificationOptionClick}
              disabled={clarificationOptionsDisabled}
              selectedOption={selectedClarificationOption}
            />
          )}
        </>
      );
    }

    if (isTable()) {
      return <TableRenderer message={message} id={id} setPaginationState={setPaginationState} />;
    }

    if (containsMarkdown()) {
      return (
        <>
          <MarkdownRenderer content={message} onChunkClick={onChunkClick} />
          {isAuthorAdmin && clarificationOptions && clarificationOptions.length > 0 && (
            <ClarificationOptions
              options={clarificationOptions}
              onOptionClick={handleClarificationOptionClick}
              disabled={clarificationOptionsDisabled}
              selectedOption={selectedClarificationOption}
            />
          )}
        </>
      );
    }

    return (
      <>
        <div className="whitespace-pre-wrap break-words text-fg">
          {Array.isArray(message)
            ? message.map((key, index) => (
                <div key={`msg-item-${key}-${index}`}>
                  <button
                    type="button"
                    className="p-0 text-left underline cursor-pointer text-brand hover:opacity-80"
                    onClick={() => handleSend(key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSend(key);
                      }
                    }}
                  >
                    {index + 1}. {key}
                  </button>
                </div>
              ))
            : message}
        </div>
        {isAuthorAdmin && clarificationOptions && clarificationOptions.length > 0 && (
          <div className="mt-3">
            <ClarificationOptions
              options={clarificationOptions}
              onOptionClick={handleClarificationOptionClick}
              disabled={clarificationOptionsDisabled}
              selectedOption={selectedClarificationOption}
            />
          </div>
        )}
      </>
    );
  };

  return renderMessageContent();
};

MessageContent.propTypes = {
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object, PropTypes.bool]).isRequired,
  isTable: PropTypes.func.isRequired,
  containsMarkdown: PropTypes.func.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleSend: PropTypes.func.isRequired,
  isAuthorAdmin: PropTypes.bool.isRequired,
  clarificationOptions: PropTypes.array,
  handleClarificationOptionClick: PropTypes.func,
  clarificationOptionsDisabled: PropTypes.bool,
  selectedClarificationOption: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  setPaginationState: PropTypes.func,
  onChunkClick: PropTypes.func,
};

// Helper component for rendering tab content
const TabContent = ({
  selectedTab,
  id,
  isSQLDatatype,
  isInsightsDatatype,
  totalRows,
  displayData,
  message,
  isTable,
  containsMarkdown,
  handleSend,
  isAuthorAdmin,
  clarificationOptions,
  handleClarificationOptionClick,
  clarificationOptionsDisabled,
  selectedClarificationOption,
  chartData,
  setChartData,
  source,
  datatype,
  description,
  curl_commands,
  setPaginationState,
  sessionId,
  isStreaming,
  perSourceData,
  effectiveTotalRows,
  onChunkClick,
}) => {
  // Centralized processing state detection
  // Check if message or description contains processing/loading indicators
  const isProcessing =
    (typeof message === 'string' && 
     (message.includes('Processing your request') || 
      message.includes('is running') ||
      message.includes('Analyzing') ||
      message.includes('Sub-agent') ||
      (message.includes('...') && message.length < 100))) ||
    (typeof description === 'string' && 
     (description.includes('is running') || 
      description.includes('Processing') ||
      description.includes('Analyzing') ||
      description.includes('Sub-agent') ||
      description.includes('task complexity')));
  
  // Determine which content to show during processing (prefer description if it has processing info)
  const processingContent = 
    (typeof description === 'string' && 
     (description.includes('Analyzing') || description.includes('Sub-agent') || description.includes('Processing') || description.includes('is running'))) 
      ? description 
      : (message || description || 'Processing...');
  
  switch (selectedTab) {
    // `data` is no longer a panel — rows render in the data rail, reached by the
    // chip under the answer. Kept out of this switch entirely so nothing can
    // render a result set back into the transcript by passing a stale tab name.
    case "chart":
      return message && message.length > 0 ? (
        <Chart source={id} data={message} chartData={chartData} setChartData={setChartData} />
      ) : (
        <p>Data required for Chart</p>
      );

    case "source":
      return <SourceTabContent source={source} isSQLDatatype={isSQLDatatype} datatype={datatype} id={id} curl_commands={curl_commands} isStreaming={isStreaming} />;

    case "description":
      return (
        <DescriptionTabContent
          description={description}
          message={message}
          isSQLDatatype={isSQLDatatype}
          datatype={datatype}
          source={source}
          id={id}
          isTable={isTable}
          containsMarkdown={containsMarkdown}
          handleSend={handleSend}
          isAuthorAdmin={isAuthorAdmin}
          clarificationOptions={clarificationOptions}
          handleClarificationOptionClick={handleClarificationOptionClick}
          clarificationOptionsDisabled={clarificationOptionsDisabled}
          selectedClarificationOption={selectedClarificationOption}
          setPaginationState={setPaginationState}
          isProcessing={isProcessing}
          processingContent={processingContent}
          isStreaming={isStreaming}
          onChunkClick={onChunkClick}
        />
      );

    case "select":
      return datatype === "error" ? <>No Source to display.</> : null;

    default:
      return null;
  }
};

TabContent.propTypes = {
  selectedTab: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isInsightsDatatype: PropTypes.bool,
  isSQLDatatype: PropTypes.bool.isRequired,
  // A multi-step turn sends one count per source, so this is a number OR a map
  // keyed by source name — which is what the "Invalid prop totalRows of type
  // object" warning has been reporting.
  totalRows: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  displayData: PropTypes.array,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]).isRequired,
  isTable: PropTypes.func.isRequired,
  containsMarkdown: PropTypes.func.isRequired,
  handleSend: PropTypes.func.isRequired,
  isAuthorAdmin: PropTypes.bool.isRequired,
  clarificationOptions: PropTypes.array,
  handleClarificationOptionClick: PropTypes.func,
  clarificationOptionsDisabled: PropTypes.bool,
  selectedClarificationOption: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  chartData: PropTypes.array.isRequired,
  setChartData: PropTypes.func.isRequired,
  source: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  datatype: PropTypes.string,
  description: PropTypes.string,
  curl_commands: PropTypes.array,
  setPaginationState: PropTypes.func,
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isStreaming: PropTypes.bool,
  onChunkClick: PropTypes.func,
};

// Helper function to highlight bash/curl syntax
const highlightBash = (line, isDarkMode) => {
  if (!line || line.trim() === '') return line || '\u00A0';
  
  // Bash/curl keywords and options
  const keywords = /\b(curl|grep|awk|sed|cat|echo|export|if|then|else|fi|for|while|do|done|function)\b/g;
  const flags = /(-[a-zA-Z]|--[a-zA-Z-]+)/g;
  
  // String literals (single and double quotes)
  const stringRegex = /(["'][^"']*["'])/g;
  const strings = [];
  let match;
  
  while ((match = stringRegex.exec(line)) !== null) {
    strings.push({ start: match.index, end: match.index + match[0].length, text: match[0] });
  }
  
  // Process line
  let currentIndex = 0;
  const segments = [];
  
  strings.forEach((str) => {
    if (str.start > currentIndex) {
      segments.push({ text: line.substring(currentIndex, str.start), type: 'code' });
    }
    segments.push({ text: str.text, type: 'string' });
    currentIndex = str.end;
  });
  
  if (currentIndex < line.length) {
    segments.push({ text: line.substring(currentIndex), type: 'code' });
  }
  
  // Render segments
  return (
    <>
      {segments.map((segment, idx) => {
        if (segment.type === 'string') {
          return <span key={idx} style={{ color: isDarkMode ? '#10b981' : '#198754' }}>{segment.text}</span>;
        } else {
          // Highlight keywords and flags in code segments
          let processedText = segment.text;
          const elements = [];
          let lastIdx = 0;
          
          // Find all keywords and flags
          const allMatches = [];
          let keywordMatch;
          const keywordRegex = new RegExp(keywords.source, 'g');
          while ((keywordMatch = keywordRegex.exec(processedText)) !== null) {
            allMatches.push({ start: keywordMatch.index, end: keywordMatch.index + keywordMatch[0].length, text: keywordMatch[0], type: 'keyword' });
          }
          
          let flagMatch;
          const flagRegex = new RegExp(flags.source, 'g');
          while ((flagMatch = flagRegex.exec(processedText)) !== null) {
            allMatches.push({ start: flagMatch.index, end: flagMatch.index + flagMatch[0].length, text: flagMatch[0], type: 'flag' });
          }
          
          // Sort by position
          allMatches.sort((a, b) => a.start - b.start);
          
          allMatches.forEach((m, mIdx) => {
            if (m.start > lastIdx) {
              elements.push(<span key={`${idx}-text-${mIdx}`}>{processedText.substring(lastIdx, m.start)}</span>);
            }
            if (m.type === 'keyword') {
              elements.push(<span key={`${idx}-kw-${mIdx}`} style={{ color: isDarkMode ? '#8b5cf6' : '#6f42c1', fontWeight: '600' }}>{m.text}</span>);
            } else {
              elements.push(<span key={`${idx}-flag-${mIdx}`} style={{ color: isDarkMode ? '#3b82f6' : '#0d6efd', fontWeight: '500' }}>{m.text}</span>);
            }
            lastIdx = m.end;
          });
          
          if (lastIdx < processedText.length) {
            elements.push(<span key={`${idx}-end`}>{processedText.substring(lastIdx)}</span>);
          }
          
          return elements;
        }
      })}
    </>
  );
};

// Helper function to highlight SQL syntax
const highlightSQL = (line, isDarkMode) => {
  if (!line || line.trim() === '') return line || '\u00A0';
  
  // SQL keywords
  const keywords = /\b(SELECT|FROM|WHERE|JOIN|INNER|LEFT|RIGHT|OUTER|ON|AND|OR|IN|AS|BETWEEN|GROUP BY|ORDER BY|HAVING|LIMIT|OFFSET|INSERT|UPDATE|DELETE|CREATE|DROP|ALTER|TABLE|INDEX|VIEW|DATABASE|UNION|ALL|DISTINCT|COUNT|SUM|AVG|MIN|MAX|COALESCE|CASE|WHEN|THEN|ELSE|END|CAST|DATE_TRUNC|INTERVAL|DATE|NULL|IS|NOT|LIKE|EXISTS|WITH)\b/gi;
  
  // Comments (-- style)
  const commentMatch = line.match(/^(\s*)(--.*)/);
  if (commentMatch) {
    return (
      <>
        {commentMatch[1]}
        <span style={{ color: isDarkMode ? '#6b7280' : '#6c757d', fontStyle: 'italic' }}>
          {commentMatch[2]}
        </span>
      </>
    );
  }
  
  // Chain of Thought line
  if (line.includes('--- Chain of Thought')) {
    return <span style={{ color: isDarkMode ? '#6b7280' : '#6c757d', fontStyle: 'italic' }}>{line}</span>;
  }
  
  // String literals
  const stringRegex = /'[^']*'/g;
  const strings = [];
  let match;
  
  while ((match = stringRegex.exec(line)) !== null) {
    strings.push({ start: match.index, end: match.index + match[0].length, text: match[0] });
  }
  
  // Process line with keyword and string highlighting
  let processedLine = line;
  const segments = [];
  let currentIndex = 0;
  
  // Split by strings first
  strings.forEach((str, idx) => {
    if (str.start > currentIndex) {
      // Process non-string part for keywords
      const beforeString = processedLine.substring(currentIndex, str.start);
      segments.push({ text: beforeString, type: 'code' });
    }
    segments.push({ text: str.text, type: 'string' });
    currentIndex = str.end;
  });
  
  if (currentIndex < processedLine.length) {
    segments.push({ text: processedLine.substring(currentIndex), type: 'code' });
  }
  
  // Render segments
  return (
    <>
      {segments.map((segment, idx) => {
        if (segment.type === 'string') {
          return <span key={idx} style={{ color: isDarkMode ? '#10b981' : '#198754' }}>{segment.text}</span>;
        } else {
          // Highlight keywords in code segments
          const keywordParts = segment.text.split(keywords);
          return keywordParts.map((part, partIdx) => {
            if (keywords.test(part)) {
              return <span key={`${idx}-${partIdx}`} style={{ color: isDarkMode ? '#8b5cf6' : '#6f42c1', fontWeight: '600' }}>{part}</span>;
            }
            return <span key={`${idx}-${partIdx}`}>{part}</span>;
          });
        }
      })}
    </>
  );
};

// Helper component for source tab content
const SourceTabContent = ({ source, isSQLDatatype, datatype, id, curl_commands, isStreaming }) => {
  const { isDarkMode } = useDarkMode();
  const hasCurlCommands = curl_commands && curl_commands.length > 0;
  const hasSQL = (isSQLDatatype || datatype === "insights") && source;

  // Show both SQL and cURL if available
  if (hasSQL || hasCurlCommands) {
    return (
      <div>
        {/* Show SQL if available */}
        {hasSQL && (
          <div className={hasCurlCommands ? "mb-4" : ""}>
            <h5 className="mb-2 font-semibold text-fg">Executed SQL:</h5>
            <div className="font-inherit text-inherit whitespace-pre-wrap">
              {Array.isArray(source) ? (
                  source.map((item, index) => {
                    // Check if item is string or object
                    const queryContent = typeof item === 'string' ? item : (item.query || JSON.stringify(item));

                    // Split by newlines and render each line separately
                    const lines = queryContent.split('\n');

                    return (
                      <div key={index} className="mb-3">
                        <div className="rounded-lg p-3 overflow-x-auto table-scrollbar font-mono text-sm leading-relaxed bg-surface-sunken text-fg">
                          {lines.map((line, lineIndex) => (
                            <div key={lineIndex} className="min-h-[1.7em]">
                              {highlightSQL(line, isDarkMode)}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="rounded-lg p-3 overflow-x-auto table-scrollbar font-mono text-sm leading-relaxed bg-surface-sunken text-fg">
                    {(typeof source === "string" ? source : JSON.stringify(source)).split('\n').map((line, lineIndex) => (
                      <div key={lineIndex} className="min-h-[1.7em]">
                        {highlightSQL(line, isDarkMode)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
          </div>
        )}

        {/* Show cURL commands if available */}
        {hasCurlCommands && (
            <div>
              <h5 className="mb-2 font-semibold text-fg">API Calls:</h5>
              {curl_commands.map((cmd, index) => {
                const curlContent = cmd.curl || "No curl command available";
                const lines = curlContent.split('\n');

                return (
                  <div key={index} className="mb-3">
                    {cmd.url && (
                      <p className="mb-2 text-fg">
                        <strong className="text-fg">URL:</strong> {cmd.url}
                      </p>
                    )}
                    <div className="rounded-lg p-3 overflow-x-auto table-scrollbar font-mono text-sm leading-relaxed bg-surface-sunken text-fg">
                      {lines.map((line, lineIndex) => (
                        <div key={lineIndex} className="min-h-[1.7em]">
                          {highlightBash(line, isDarkMode)}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    );
  }

  if (datatype === "documents") {
    return (
      <div className="input-group">
        {source.length > 0 ? (
          source.map((msg, index) => <SourceCard key={`${id}-${index}`} msg={msg} chatMessageId={id} />)
        ) : isStreaming ? (
          <span className="text-fg">Sources are being generated…</span>
        ) : (
          <>No Source to display.</>
        )}
      </div>
    );
  }

  if (datatype === "error") {
    return <>No Source to display.</>;
  }

  if (isStreaming) {
    return <span className="text-fg">Sources are being generated…</span>;
  }

  return <>No Source to display.</>;
};

SourceTabContent.propTypes = {
  source: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  isSQLDatatype: PropTypes.bool.isRequired,
  datatype: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  curl_commands: PropTypes.array,
  isStreaming: PropTypes.bool,
};

// Helper component for description tab content
const DescriptionTabContent = ({ description, message, isSQLDatatype, datatype, source, id, isTable, containsMarkdown, handleSend, isAuthorAdmin, clarificationOptions, handleClarificationOptionClick, clarificationOptionsDisabled, selectedClarificationOption, setPaginationState, isProcessing, processingContent, isStreaming, onChunkClick }) => {
  // If processing, show the same loading indicator as in Data tab using centralized processingContent
  if (isProcessing) {
    return (
      <div className="whitespace-pre-wrap break-words text-fg">
        <MarkdownRenderer content={processingContent} />
      </div>
    );
  }

  // Render HTML descriptions for any datatype
  if (looksLikeHTML(description)) {
    return (
      <div
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }}
        onClick={(e) => {
          const a = e.target.closest('a[href^="#cite-"]');
          if (a && onChunkClick) {
            e.preventDefault();
            onChunkClick(parseInt(a.getAttribute('href').slice(6), 10));
          }
        }}
      />
    );
  }

  // For insights datatype, show the description as markdown
  if (datatype === "insights") {
    if (description) {
      return <MarkdownRenderer content={description} onChunkClick={onChunkClick} />;
    }
    if (isStreaming) return <span className="text-fg">Insights are being generated…</span>;
    return <span className="text-fg">No description available</span>;
  }

  // For SQL datatype, the message is table/records data — never render it in the Insights tab.
  // Show description if present, otherwise a friendly placeholder.
  if (isSQLDatatype) {
    if (description) return <MarkdownRenderer content={description} onChunkClick={onChunkClick} />;
    if (isStreaming) return <span className="text-fg">Insights are being generated…</span>;
    return <span className="text-fg-muted">No description available</span>;
  }

  // For text datatype or when there's a description, show it
  if (description) {
    return <MarkdownRenderer content={description} onChunkClick={onChunkClick} />;
  }

  // Otherwise show the message content
  return (
    <MessageContent
      message={message}
      isTable={isTable}
      containsMarkdown={containsMarkdown}
      id={id}
      handleSend={handleSend}
      isAuthorAdmin={isAuthorAdmin}
      clarificationOptions={clarificationOptions}
      handleClarificationOptionClick={handleClarificationOptionClick}
      clarificationOptionsDisabled={clarificationOptionsDisabled}
      selectedClarificationOption={selectedClarificationOption}
      setPaginationState={setPaginationState}
      onChunkClick={onChunkClick}
    />
  );
};

DescriptionTabContent.propTypes = {
  description: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]).isRequired,
  isSQLDatatype: PropTypes.bool.isRequired,
  datatype: PropTypes.string,
  source: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isTable: PropTypes.func.isRequired,
  containsMarkdown: PropTypes.func.isRequired,
  handleSend: PropTypes.func.isRequired,
  isAuthorAdmin: PropTypes.bool.isRequired,
  clarificationOptions: PropTypes.array,
  handleClarificationOptionClick: PropTypes.func,
  clarificationOptionsDisabled: PropTypes.bool,
  selectedClarificationOption: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  setPaginationState: PropTypes.func,
  isProcessing: PropTypes.bool,
  processingContent: PropTypes.string,
  isStreaming: PropTypes.bool,
  onChunkClick: PropTypes.func,
};

/**
 * Renders content directly without tabs — used for rag, clarification, and unknown answer types.
 * For rag: renders the insights/description content (markdown or HTML).
 * For clarification/unknown: renders message content with clarification options.
 */
const DirectContent = ({
  answer_type,
  railOwnsRows,
  analysisMode,
  message,
  description,
  dataSourcesMap,
  replaceCitationMarkers,
  isTable,
  containsMarkdown,
  id,
  handleSend,
  isAuthorAdmin,
  clarificationOptions,
  handleClarificationOptionClick,
  clarificationOptionsDisabled,
  selectedClarificationOption,
  setPaginationState,
  isStreaming,
  onChunkClick,
}) => {
  if (answer_type === 'rag') {
    const processCitations = (text) => replaceCitationMarkers ? replaceCitationMarkers(text) : text;

    // Renders the curated insights (key_insights + actionable_recommendations,
    // carried in `description`) as HTML or markdown. Returns null when there's none.
    const renderInsights = () => {
      if (!description) return null;
      if (looksLikeHTML(description)) {
        return <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }} />;
      }
      return <MarkdownRenderer content={description} onChunkClick={onChunkClick} />;
    };

    // Renders a single data_sources value: raw HTML, markdown with citations,
    // or JSON-stringified markdown for non-string values.
    const renderDataSourceValue = (val) => {
      if (typeof val !== 'string') {
        return <MarkdownRenderer content={JSON.stringify(val)} onChunkClick={onChunkClick} />;
      }
      if (looksLikeHTML(val)) {
        return <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(val) }} />;
      }
      return <MarkdownRenderer content={processCitations(val)} onChunkClick={onChunkClick} />;
    };

    // Renders the raw data_sources values directly, with citations processed.
    // Returns null when there are no data sources to show.
    //
    // `railOwnsRows` is how a reloaded turn stops printing its table twice: the
    // rail holds the rows now, and this path is what used to render them inline
    // for exactly the payloads that carry no answer_type.
    const renderDataSources = () => {
      if (railOwnsRows) return null;
      if (!dataSourcesMap || typeof dataSourcesMap !== 'object') return null;
      const entries = Object.entries(dataSourcesMap);
      if (entries.length === 0) return null;
      return (
        <div>
          {entries.map(([key, val]) => (
            <div key={key}>{renderDataSourceValue(val)}</div>
          ))}
        </div>
      );
    };

    // Deep ("thinking") mode produces key_insights + actionable_recommendations —
    // always show those, never the raw retrieved chunks. Other modes are RAG
    // passthroughs where data_sources IS the answer, so show that first and only
    // fall back to insights when there are no data sources.
    const insights = renderInsights();
    const sources = renderDataSources();
    const primary = analysisMode === 'deep'
      ? (insights || sources)
      : (sources || insights);
    if (primary) return primary;

    if (message) {
      return <MarkdownRenderer content={typeof message === 'string' ? message : JSON.stringify(message)} onChunkClick={onChunkClick} />;
    }
    return <span className="text-fg-muted">No content available</span>;
  }

  // unknown answer with no message (e.g. answer_summary content, empty key_insights):
  // fall back to description instead of showing a blank bubble.
  const hasMessage = typeof message === 'string' ? message.trim() : message;
  if (!hasMessage && description && !clarificationOptions?.length) {
    if (looksLikeHTML(description)) {
      return <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }} />;
    }
    return <MarkdownRenderer content={description} onChunkClick={onChunkClick} />;
  }

  // clarification / unknown: render message content with clarification options
  return (
    <MessageContent
      message={message}
      isTable={isTable}
      containsMarkdown={containsMarkdown}
      id={id}
      handleSend={handleSend}
      isAuthorAdmin={isAuthorAdmin}
      clarificationOptions={clarificationOptions}
      handleClarificationOptionClick={handleClarificationOptionClick}
      clarificationOptionsDisabled={clarificationOptionsDisabled}
      selectedClarificationOption={selectedClarificationOption}
      setPaginationState={setPaginationState}
      onChunkClick={onChunkClick}
    />
  );
};

DirectContent.propTypes = {
  answer_type: PropTypes.string,
  analysisMode: PropTypes.oneOf(["deep", "fast"]),
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  description: PropTypes.string,
  dataSourcesMap: PropTypes.object,
  railOwnsRows: PropTypes.bool,
  replaceCitationMarkers: PropTypes.func,
  isTable: PropTypes.func,
  containsMarkdown: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleSend: PropTypes.func.isRequired,
  isAuthorAdmin: PropTypes.bool.isRequired,
  clarificationOptions: PropTypes.array,
  handleClarificationOptionClick: PropTypes.func,
  clarificationOptionsDisabled: PropTypes.bool,
  selectedClarificationOption: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  setPaginationState: PropTypes.func,
  isStreaming: PropTypes.bool,
  onChunkClick: PropTypes.func,
};

// Helper component for feedback modal
const FeedbackModal = ({ showModal, feedbackComment, setFeedbackComment, handleModalSubmit, handleModalClose, isSubmitting }) => {
  const isValid = feedbackComment.trim().length >= 1;

  if (!showModal) return null;

  return (
    <>
      {/* Backdrop with blur */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] transition-opacity"
        onClick={handleModalClose}
      />

      {/* Modal - Increased width to match reference */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div
          className="relative w-full max-w-lg rounded-xl shadow-2xl bg-surface"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleModalClose}
            disabled={isSubmitting}
            className={`absolute top-4 right-4 p-1 rounded-lg transition-colors text-fg-muted hover:text-fg hover:bg-surface-raised ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header */}
          <div className="p-6 pb-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-brand-soft">
                {/* Thumbs down icon instead of message bubble */}
                <ThumbsDown className="w-5 h-5 text-brand" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-1 text-fg">
                  Tell us what went wrong
                </h3>
                <p className="text-sm text-fg-muted">
                  Your feedback helps us improve response quality.
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 pb-4">
            <textarea
              rows={4}
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              placeholder="What didn't work as expected?"
              disabled={isSubmitting}
              className={`w-full rounded-lg px-4 py-3 border-2 border-line focus:outline-none focus:ring-0 transition-colors resize-none custom-scrollbar bg-surface-raised text-fg focus:border-brand placeholder:text-fg-muted ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-line">
            <button
              onClick={handleModalClose}
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-lg font-medium transition-colors bg-surface-raised hover:bg-surface text-fg ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Cancel
            </button>
            <button
              onClick={handleModalSubmit}
              disabled={!isValid || isSubmitting}
              className={`px-5 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                isValid && !isSubmitting
                  ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow-md'
                  : 'bg-teal-600 text-white cursor-not-allowed opacity-60'
              }`}
            >
              {isSubmitting && <Spinner className="h-4 w-4" />}
              {isSubmitting ? 'Sending...' : 'Send feedback'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

FeedbackModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  feedbackComment: PropTypes.string.isRequired,
  setFeedbackComment: PropTypes.func.isRequired,
  handleModalSubmit: PropTypes.func.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

// Helper component for action buttons
const CostModal = ({ costData, costError, onClose, isDarkMode }) => {
  // Which call row's details are currently expanded — null = all collapsed.
  // Per-row toggle matches the old per-component UX users were used to and
  // avoids the visual crowding of showing tokens + model on every row at once.
  const [expandedCallId, setExpandedCallId] = useState(null);

  const formatMs = (val) => {
    if (val == null) return '-';
    const ms = parseInt(val, 10);
    if (isNaN(ms)) return val;
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${ms}ms`;
  };

  // Render the producer's started_at (ISO 8601 UTC) as HH:MM:SS in the
  // user's local timezone. Falls back to em-dash when the row pre-dates the
  // started_at column rollout (NULL → string '' from json normalization).
  // Sub-second precision dropped for display readability — backend ORDER BY
  // still uses microsecond precision so close-together calls retain correct
  // ordering even when their displayed timestamps collide.
  const formatStartedAt = (iso) => {
    if (!iso) return '—';
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return '—';
    const hh = String(dt.getHours()).padStart(2, '0');
    const mm = String(dt.getMinutes()).padStart(2, '0');
    const ss = String(dt.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  };

  const d = isDarkMode;
  const bg = 'bg-surface';
  const border = 'border-line';
  const text = 'text-fg';
  const sub = 'text-fg-muted';
  const cardBg = 'bg-surface-raised';
  const hoverRow = 'hover:bg-surface-raised';
  const expandBg = 'bg-surface-raised';

  const TOKEN_TYPES = [
    { key: ['input_tokens', 'input'],     label: 'Input',     color: d ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-blue-50 text-blue-600 border-blue-200' },
    { key: ['output_tokens', 'output'],   label: 'Output',    color: d ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    { key: ['thinking_tokens', 'thinking'], label: 'Thinking', color: d ? 'bg-orange-500/20 text-orange-300 border-orange-500/30' : 'bg-orange-50 text-orange-600 border-orange-200' },
    { key: ['cached_tokens', 'cached'],         label: 'Cached',         color: d ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' : 'bg-purple-50 text-purple-600 border-purple-200' },
    { key: ['cache_storage_tokens'],             label: 'Cache Storage',  color: d ? 'bg-teal-500/20 text-teal-300 border-teal-500/30' : 'bg-teal-50 text-teal-600 border-teal-200' },
    { key: ['embedding_tokens', 'embedding'],    label: 'Embedding',      color: d ? 'bg-pink-500/20 text-pink-300 border-pink-500/30' : 'bg-pink-50 text-pink-600 border-pink-200' },
  ];

  const getToken = (breakdown, keys) => {
    for (const k of keys) if (breakdown?.[k] != null) return breakdown[k];
    return null;
  };

  const TokenRow = ({ breakdown }) => {
    if (!breakdown) return null;
    const tokens = TOKEN_TYPES.map(t => ({ ...t, val: getToken(breakdown, t.key) })).filter(t => t.val != null && t.val > 0);
    if (!tokens.length) return null;
    return (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {tokens.map(t => (
          <span key={t.label} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md border font-mono ${t.color}`}>
            <span className={`text-[10px] font-sans opacity-70`}>{t.label}</span>
            {t.val.toLocaleString()}
          </span>
        ))}
      </div>
    );
  };

  const StatCard = ({ label, value, valueClass }) => (
    <div className={`flex flex-col gap-0.5 px-4 py-3 rounded-xl ${cardBg}`}>
      <span className={`text-[11px] font-medium uppercase tracking-wider ${sub}`}>{label}</span>
      <span className={`text-sm font-bold ${valueClass || text}`}>{value}</span>
    </div>
  );

  // Op-type badge palette — matches the existing TOKEN_TYPES color scheme:
  //   LLM=blue, EMB=pink, CACHE=teal, SQL=emerald, API=amber.
  // Each call row shows one of these as a small leading pill so users can
  // scan op-types at a glance in the chronological waterfall.
  const OP_BADGE = {
    llm_call: {
      label: 'LLM',
      cls: d
        ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
        : 'bg-blue-50 text-blue-600 border-blue-200',
      Icon: MessageSquare,
    },
    embedding_call: {
      label: 'EMB',
      cls: d
        ? 'bg-pink-500/20 text-pink-300 border-pink-500/30'
        : 'bg-pink-50 text-pink-600 border-pink-200',
      Icon: Hash,
    },
    cache_storage: {
      label: 'CACHE CREATED',
      cls: d
        ? 'bg-teal-500/20 text-teal-300 border-teal-500/30'
        : 'bg-teal-50 text-teal-600 border-teal-200',
      Icon: HardDrive,
    },
    sql_query: {
      label: 'SQL',
      cls: d
        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
        : 'bg-emerald-50 text-emerald-700 border-emerald-200',
      Icon: Database,
    },
    api_call: {
      label: 'API',
      cls: d
        ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
        : 'bg-amber-50 text-amber-700 border-amber-200',
      Icon: Globe,
    },
    // Fallback for unknown / future op_types. Prefer surfacing the raw
    // operationType in the label (uppercased) so users see WHAT it is
    // rather than silently mislabeling it as LLM during a rollout window
    // where a new producer op_type ships ahead of the chat-UI deploy.
    unknown: {
      label: 'UNKNOWN',
      cls: d
        ? 'bg-slate-500/20 text-slate-300 border-slate-500/30'
        : 'bg-slate-100 text-slate-600 border-slate-300',
      Icon: HelpCircle,
    },
  };

  // Render the cost badge for a single call row — color matches OP_BADGE so
  // the cost and op-type read as the same thing visually.
  //
  // NOTE: this is a plain helper function (call via `renderCallCostBadge(call)`)
  // — NOT a component. Defining it as a component (`<CallCostBadge ... />`)
  // inside CostModal would mean React sees a fresh component type on every
  // render (`expandedCallId` flips), causing every row to unmount + remount
  // and lose DOM state. Plain helpers don't trigger that; they're just inline
  // JSX returned from a function call.
  //
  // Fallback handling: for ops outside the (llm / embedding / cache_storage)
  // colorMap that still carry a costUsd (future op types, or sql_query /
  // api_call if they ever get priced), render a neutral cost badge so the
  // value is surfaced rather than silently dropped.
  const renderCallCostBadge = (call) => {
    if (call.costUsd == null) return null;
    const colorMap = {
      llm_call: d ? 'text-blue-300' : 'text-blue-600',
      embedding_call: d ? 'text-pink-300' : 'text-pink-600',
      cache_storage: d ? 'text-teal-300' : 'text-teal-600',
    };
    const labelMap = {
      llm_call: 'LLM',
      embedding_call: 'EMB',
      cache_storage: 'CACHE',
    };
    const cls = colorMap[call.operationType] ?? 'text-fg-muted';
    const lbl =
      labelMap[call.operationType] ??
      (call.operationType
        ? call.operationType.toUpperCase().replace(/_/g, ' ')
        : 'COST');
    return (
      <span className={`text-xs font-semibold tabular-nums ${cls}`}>
        {lbl} {formatCost(call.costUsd)}
      </span>
    );
  };

  // Render the per-call token-chip row — same color palette as TokenRow but
  // reads the flat token fields (inputTokens, outputTokens, etc.) directly
  // off a CallEntry. cache_storage rows route cachedTokens (= cache size) to
  // the teal cache_storage chip; everything else routes it to the purple
  // cached chip (= prompt-cache hit tokens).
  //
  // NOTE: plain helper (not a component) for the same React-identity reason
  // documented on renderCallCostBadge.
  const renderCallTokenChips = (call) => {
    const isCacheStorage = call.operationType === 'cache_storage';
    const chips = [
      { label: 'Input', val: call.inputTokens, color: TOKEN_TYPES[0].color },
      { label: 'Output', val: call.outputTokens, color: TOKEN_TYPES[1].color },
      { label: 'Thinking', val: call.thinkingTokens, color: TOKEN_TYPES[2].color },
      {
        label: 'Cached',
        val: isCacheStorage ? null : call.cachedTokens,
        color: TOKEN_TYPES[3].color,
      },
      {
        label: 'Cache Storage',
        val: isCacheStorage ? call.cachedTokens : null,
        color: TOKEN_TYPES[4].color,
      },
      {
        label: 'Embedding',
        val: call.embeddingTokens,
        color: TOKEN_TYPES[5].color,
      },
    ].filter((c) => c.val != null && c.val > 0);
    if (!chips.length) return null;
    return (
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {chips.map((c) => (
          <span
            key={c.label}
            className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border font-mono ${c.color}`}
          >
            <span className="text-[10px] font-sans opacity-70">{c.label}</span>
            {c.val.toLocaleString()}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border shadow-2xl ${bg} ${border}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-3.5 border-b ${border} shrink-0`}>
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${d ? 'bg-green-500/20' : 'bg-green-50'}`}>
              <DollarSign className={`w-4 h-4 ${d ? 'text-green-400' : 'text-green-600'}`} />
            </div>
            <h2 className={`text-sm font-semibold ${text}`}>Cost Breakdown</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg transition-colors text-fg-muted hover:text-fg hover:bg-surface-raised">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {costError ? (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-danger-soft border border-danger text-danger text-sm">
              {costError}
            </div>
          ) : !costData ? null : (
            <>
              {/* Total cost hero */}
              <div className={`p-4 rounded-xl ${d ? 'bg-gradient-to-br from-green-900/40 to-emerald-900/20 border border-green-700/30' : 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200'}`}>
                <p className={`text-xs font-medium uppercase tracking-wider mb-1 ${d ? 'text-green-400/70' : 'text-green-600/70'}`}>Total Cost</p>
                <p className={`text-2xl font-bold ${d ? 'text-green-300' : 'text-green-700'}`}>
                   {formatCost(costData.totalCost ?? ((costData.llmCost ?? 0) + (costData.embeddingCost ?? 0) + (costData.cacheStorageCost ?? 0)))}
                </p>
              </div>

              {/* Stat cards */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatCard label="LLM Cost" value={formatCost(costData.llmCost)} valueClass={d ? 'text-blue-300' : 'text-blue-600'} />
                {costData.embeddingCost > 0 && <StatCard label="Embedding" value={formatCost(costData.embeddingCost)} valueClass={d ? 'text-pink-300' : 'text-pink-600'} />}
                {costData.cacheStorageCost > 0 && <StatCard label="Cache Storage" value={formatCost(costData.cacheStorageCost)} valueClass={d ? 'text-teal-300' : 'text-teal-600'} />}
                <StatCard label="Duration" value={formatMs(costData.duration)} />
                {costData.llmCalls != null && <StatCard label="LLM Calls" value={costData.llmCalls} />}
              </div>

              {/* Total token breakdown */}
              {costData.breakdown && (
                <div>
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${sub}`}>Total Tokens</p>
                  <TokenRow breakdown={costData.breakdown} />
                </div>
              )}

              {/* Session Title (one call row emitted by the post-answer
                  background label generation. Backend extracts it from the
                  flat call list and returns it as a single CallEntry). */}
              {costData.sessionTitle && (
                <div>
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${sub}`}>Session</p>
                  <div className={`px-4 py-3 rounded-xl border ${border} ${cardBg}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded-md font-medium bg-surface-raised text-fg-secondary">
                          Session Created
                        </span>
                        {costData.sessionTitle.modelName && (
                          <span className={`text-xs ${sub} font-mono`}>{costData.sessionTitle.modelName}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs ${sub}`}>{formatMs(costData.sessionTitle.duration)}</span>
                        <span className={`text-xs font-semibold ${d ? 'text-blue-300' : 'text-blue-600'}`}>{formatCost(costData.sessionTitle.costUsd)}</span>
                      </div>
                    </div>
                    {renderCallTokenChips(costData.sessionTitle)}
                  </div>
                </div>
              )}

              {/* Call timeline — flat chronological waterfall of every
                  observability event (llm_call / embedding_call /
                  cache_storage / sql_query / api_call), ordered by
                  started_at. Backend filters session_title rows into the
                  separate sessionTitle field above.

                  Each row collapsed by default — clicking expands to show
                  token chips + model + extras. Same UX pattern as the
                  previous per-component card; just one card per call
                  instead of one per (agent, component) tuple. */}
              {costData.details && costData.details.length > 0 && (
                <div>
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${sub}`}>Call Timeline</p>
                  <div className="space-y-1.5">
                    {costData.details.map((call) => {
                      // Default to UNKNOWN (not llm_call) so a producer-side
                      // op_type added before the chat-UI ships an update
                      // surfaces as 'UNKNOWN' rather than mislabeling as LLM.
                      const badge = OP_BADGE[call.operationType] ?? OP_BADGE.unknown;
                      const BadgeIcon = badge.Icon;
                      const isError = call.status && call.status !== 'success';
                      const isExpanded = expandedCallId === call.id;
                      const rowBorder = isError ? 'border-danger' : border;
                      return (
                        <div
                          key={call.id}
                          className={`rounded-xl border ${rowBorder} overflow-hidden`}
                        >
                          {/* Collapsed view — kept minimal so the timeline
                              stays scannable. Op-type badge + token chips +
                              model live in the dropdown (above) — the cost
                              color already telegraphs op-type (LLM=blue,
                              EMB=pink, CACHE=teal); SQL / API have no cost so
                              expanding is how the user confirms what they
                              are. */}
                          <button
                            type="button"
                            aria-expanded={isExpanded}
                            className={`w-full flex items-center justify-between px-3 py-2.5 transition-colors ${hoverRow}`}
                            onClick={() =>
                              setExpandedCallId(isExpanded ? null : call.id)
                            }
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className={`text-[11px] font-mono tabular-nums shrink-0 ${sub}`}>
                                {formatStartedAt(call.startedAt)}
                              </span>
                              {call.agentName && (
                                <span className={`text-xs px-2 py-0.5 rounded-md font-medium shrink-0 ${d ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                                  {call.agentName}
                                </span>
                              )}
                              {call.componentName && (
                                <span className={`text-sm font-medium truncate ${text}`}>{call.componentName}</span>
                              )}
                              {/* callLabel: human-readable name for the
                                  semantic LLM call. Rendered as muted
                                  inline text after the component name with
                                  a "·" separator (NOT as a pill) — natural
                                  read order is "what (component) is doing
                                  what (label)", and a third colored pill
                                  after the agent pill would crowd the
                                  narrow chat-modal row. Indigo color keeps
                                  it visually distinct from the gray
                                  component name without competing with the
                                  amber agent pill for attention.

                                  Separator is conditional on componentName
                                  being present — without it, a leading
                                  "·" would orphan in front of the label
                                  text. In that case the label renders
                                  naked. */}
                              {call.callLabel && (
                                <>
                                  {call.componentName && (
                                    <span className={`text-xs ${sub}`}>·</span>
                                  )}
                                  <span className={`text-xs font-medium truncate ${d ? 'text-indigo-300' : 'text-indigo-600'}`}>
                                    {call.callLabel}
                                  </span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-2.5 shrink-0 ml-3">
                              <span className={`text-xs tabular-nums ${sub}`}>{formatMs(call.duration)}</span>
                              {renderCallCostBadge(call)}
                              {isExpanded ? (
                                <ChevronUp className={`w-3.5 h-3.5 ${sub}`} />
                              ) : (
                                <ChevronDown className={`w-3.5 h-3.5 ${sub}`} />
                              )}
                            </div>
                          </button>
                          {isExpanded && (
                            <div className={`px-3 pb-3 pt-2 border-t ${border} ${expandBg} space-y-2`}>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md border ${badge.cls}`}>
                                  <BadgeIcon className="w-3 h-3" />
                                  {badge.label}
                                </span>
                                {call.modelName && (
                                  <p className={`text-xs ${sub}`}>
                                    Model: <span className={`font-mono ${text}`}>{call.modelName}</span>
                                  </p>
                                )}
                                {call.operationType === 'cache_storage' && call.cacheTtlSeconds != null && (
                                  <p className={`text-xs ${sub}`}>
                                    TTL: <span className={`font-medium ${text}`}>{call.cacheTtlSeconds}s</span>
                                  </p>
                                )}
                                {call.operationType === 'cache_storage' && call.cachedTokens != null && (
                                  <p className={`text-xs ${sub}`}>
                                    Size: <span className={`font-medium ${text}`}>{call.cachedTokens.toLocaleString()} tokens</span>
                                  </p>
                                )}
                              </div>
                              {renderCallTokenChips(call)}
                              {isError && call.errorMessage && (
                                <p className="text-[11px] text-danger">
                                  {call.errorMessage}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

CostModal.propTypes = {
  costData: PropTypes.object,
  costError: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  isDarkMode: PropTypes.bool,
};

const ActionButtons = ({
  selectedTab,
  copyTab,
  isSQLDatatype,
  source,
  message,
  description,
  question,
  timestamp,
  dataSourcesMap,
  curl_commands,
  onReply,
  isAuthorAdmin,
  feedback,
  handleFeedback,
  handleDislikeClick,
  id,
  chatSessionId,
  paginationState,
  isStreaming,
  onShowCost,
  costLoading,
  bare = false,
}) => {
  const { isDarkMode } = useDarkMode();
  const { user } = useAuth();
  const toast = useToast();

  // Visible for any RCA-formatted answer (Symptoms / Environment / Resolution /
  // Root Cause / Workaround / Defect / Knowledge Article — docs/qad/harness/
  // prompt_v12/rca_answer_format.md on the `discover` side), not admin-gated like the
  // generic Download menu below: asking a root-cause question is an ordinary
  // user action, and the point of this button is to hand that analysis to
  // whatever actually tracks tickets.
  //
  // The backend's structured envelope (answer_summary/key_insights/...) splits
  // across two fields once the client parses it (responseParser.js's
  // processJSONContent): an "insights"-datatype turn like this one comes back
  // with `message` EMPTY and the actual written analysis in `description` — the
  // same reason the admin Download button above checks `description` first in
  // hasExportableText. Checking only `message` here matched nothing, ever.
  const isRca = isRcaAnswer(description) || isRcaAnswer(message);

  const handleCreateTicket = () => {
    const confirmed = window.confirm(
      'Create a ticket from this root-cause analysis?\n\nTicket creation isn’t wired up yet, so this will download the analysis as a Markdown file instead.'
    );
    if (!confirmed) return;
    const markdown = buildMarkdown({
      question,
      answer: message,
      description,
      askedAt: timestamp ? toMessageDate(timestamp) : undefined,
      exportedBy: user?.name,
    });
    if (!downloadTextFile(markdown, exportFilename(question, 'md'))) {
      toast?.error?.('Could not save the file. Check the browser download settings.');
    }
  };

  // `bare` is the toolbar under a plain prose answer, which no longer sits in a
  // card: no top rule to align to, and no horizontal padding of its own.
  return (
    <div className={bare
      ? "flex items-center justify-between py-1"
      : "flex items-center justify-between px-4 py-2 border-t border-line"}>
      {/* Left side: Action buttons */}
      <div className="flex items-center gap-1">
        <CopyButton
          // Copy targets the answer, not whatever supporting panel is open —
          // the answer never leaves the page now, so it is always copyable.
          selectedTab={copyTab ?? selectedTab}
          copyText={message}
          source={source}
          curl_commands={curl_commands}
          description={description}
          dataSourcesMap={dataSourcesMap}
        />
        {isRca && !isStreaming && (
          <Tooltip text="Create Ticket" isDarkMode={isDarkMode}>
            <button
              type="button"
              className="flex items-center gap-1.5 px-2 py-2 rounded-lg transition-colors text-fg-muted hover:text-fg hover:bg-surface-raised"
              onClick={handleCreateTicket}
            >
              <Ticket className="w-4 h-4" />
              <span className="text-xs font-medium">Create Ticket</span>
            </button>
          </Tooltip>
        )}
        {isAuthorAdmin && (
          <>
            {/* Reply button temporarily hidden */}
            {/* <Tooltip text="Reply" isDarkMode={isDarkMode}>
              <button
                className="p-2 rounded-lg transition-colors text-fg-muted hover:text-fg hover:bg-surface-raised"
                onClick={onReply}
              >
                <Reply className="w-4 h-4" />
              </button>
            </Tooltip> */}
            <Tooltip text="Like" isDarkMode={isDarkMode}>
              <button
                className={`p-2 rounded-lg transition-colors ${
                  feedback === 1
                    ? 'text-success'
                    : 'text-fg-muted hover:text-fg hover:bg-surface-raised'
                }`}
                onClick={() => handleFeedback(1)}
              >
                <ThumbsUp className={`w-4 h-4 ${feedback === 1 ? 'fill-current' : ''}`} />
              </button>
            </Tooltip>
            <Tooltip text="Dislike" isDarkMode={isDarkMode}>
              <button
                className={`p-2 rounded-lg transition-colors ${
                  feedback === -1
                    ? 'text-danger'
                    : 'text-fg-muted hover:text-fg hover:bg-surface-raised'
                }`}
                onClick={handleDislikeClick}
              >
                <ThumbsDown className={`w-4 h-4 ${feedback === -1 ? 'fill-current' : ''}`} />
              </button>
            </Tooltip>
            <Tooltip text="View Cost" isDarkMode={isDarkMode}>
              <button
                className={`p-2 rounded-lg transition-colors text-fg-muted hover:text-success hover:bg-surface-raised ${costLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={onShowCost}
                disabled={costLoading || isStreaming}
              >
                {costLoading
                  ? <span className="w-4 h-4 block rounded-full border-2 border-current border-t-transparent animate-spin" />
                  : <DollarSign className="w-4 h-4" />
                }
              </button>
            </Tooltip>
          </>
        )}
        {isAuthorAdmin && (
          <Options
            id={id}
            sessionId={chatSessionId}
            tooltipText="Download"
            canDownloadData={false}
            answer={{
              question,
              answer: message,
              description,
              askedAt: timestamp ? toMessageDate(timestamp) : undefined,
            }}
            // canDownloadData is false here, so the menu only ever offers the
            // answer exports — match its disabled state to whether there is
            // actually anything for hasExportableText to export.
            disabled={isStreaming || !hasExportableText({ description, answer: message })}
          />
        )}
      </div>

      {/* Right side: Pagination controls (only for client-side pagination, not server-side) */}
      {paginationState && paginationState.showPagination && !paginationState.isServerSide && (
        <div className="flex items-center gap-4">
          {/* Loading indicator and record count */}
          <div className="flex items-center gap-2">
            {paginationState.isLoading && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25 stroke-current"
                  cx="12"
                  cy="12"
                  r="10"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75 fill-current"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            <span className="text-sm text-fg-muted">
              {paginationState.startRecord}-{paginationState.endRecord} of {paginationState.totalRows}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Previous button */}
            <button
              onClick={() => paginationState.handlePageChange(paginationState.pageNum - 1)}
              disabled={paginationState.pageNum === 1 || paginationState.isLoading}
              title="Previous page"
              className={`p-1 rounded-md transition-colors ${
                paginationState.pageNum === 1 || paginationState.isLoading
                  ? 'text-fg-muted opacity-40 cursor-not-allowed'
                  : 'text-fg-muted hover:bg-surface-raised hover:text-fg'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Next button */}
            <button
              onClick={() => paginationState.handlePageChange(paginationState.pageNum + 1)}
              disabled={paginationState.pageNum === paginationState.totalPages || paginationState.isLoading}
              title="Next page"
              className={`p-1 rounded-md transition-colors ${
                paginationState.pageNum === paginationState.totalPages || paginationState.isLoading
                  ? 'text-fg-muted opacity-40 cursor-not-allowed'
                  : 'text-fg-muted hover:bg-surface-raised hover:text-fg'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

ActionButtons.propTypes = {
  selectedTab: PropTypes.string.isRequired,
  copyTab: PropTypes.string,
  isSQLDatatype: PropTypes.bool.isRequired,
  source: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]).isRequired,
  description: PropTypes.string,
  timestamp: PropTypes.number,
  dataSourcesMap: PropTypes.object,
  curl_commands: PropTypes.array,
  onReply: PropTypes.func.isRequired,
  isAuthorAdmin: PropTypes.bool.isRequired,
  feedback: PropTypes.number.isRequired,
  handleFeedback: PropTypes.func.isRequired,
  handleDislikeClick: PropTypes.func.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  chatSessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  paginationState: PropTypes.shape({
    isLoading: PropTypes.bool,
    pageNum: PropTypes.number,
    totalPages: PropTypes.number,
    startRecord: PropTypes.number,
    endRecord: PropTypes.number,
    // A multi-step turn sends one count per source, so this is a number OR a map
  // keyed by source name — which is what the "Invalid prop totalRows of type
  // object" warning has been reporting.
  totalRows: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    handlePageChange: PropTypes.func,
    showPagination: PropTypes.bool,
    isServerSide: PropTypes.bool,
  }),
  isStreaming: PropTypes.bool,
  onShowCost: PropTypes.func,
  costLoading: PropTypes.bool,
  bare: PropTypes.bool,
};

// Actions and the timestamp wait for intent rather than sitting on every
// answer. `hover: none` means a touch device, which has no intent to wait for —
// there the controls are simply always visible.
const REVEAL_ON_INTENT = 'opacity-0 transition-opacity duration-[var(--d-base)] ease-[var(--ease-out)] '
  + 'group-hover/msg:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100';

const ChatMessage = ({
  id,
  authorId,
  author,
  avatar,
  message,
  source,
  description,
  totalRows,
  dataSourcesMap,
  datatype,
  question,
  parentMessage,
  resizeInput,
  setQuotedMessage,
  setCurrentParentId,
  chatSessionId,
  setLiveChat,
  clarificationOptions = [],
  onClarificationOptionClick,
  clarificationOptionsDisabled = false,
  selectedClarificationOption = null,
  curl_commands = [],
  initialFeedback = 0,
  timestamp,
  isStreaming = false,
  analysis_mode,
  reference,
  answer_type,
  reasoning_steps = [],
  // Rows do not render here any more: this asks the screen to open them beside
  // the answer, and `isDataOpen` is whether this turn is the one on show.
  onOpenData,
  isDataOpen = false,
}) => {
  const { config } = useAgentConfig();
  const { getAccessToken } = useAuth();
  // A useCallback closes over the accessor from the render that created it, so a
  // memoized callback that omits getAccessToken from its deps keeps calling the
  // OLD one — which reads the pre-refresh keycloakUser and returns a token that
  // is about to 401. A ref, refreshed by its own effect, is what actually gives
  // "read fresh at call time". Same pattern as MainContent.jsx.
  const getAccessTokenRef = useRef(getAccessToken);
  useEffect(() => { getAccessTokenRef.current = getAccessToken; }, [getAccessToken]);
  const { isDarkMode } = useDarkMode();
  // Which supporting panels (the table, the query) the reader has opened. A set
  // rather than one selection: they are disclosures under the answer now, not
  // tabs competing for one slot, so more than one can be open.
  const [openPanels, setOpenPanels] = useState(() => new Set());
  const messageContainerRef = useRef(null);
  const tabChangeAnchorRef = useRef(null); // { containerTop } snapshot before a panel toggles

  const togglePanel = (panel) => {
    if (messageContainerRef.current) {
      tabChangeAnchorRef.current = {
        containerTop: messageContainerRef.current.getBoundingClientRect().top,
      };
    }
    // Suppress native scroll anchoring for the duration of the expand so the
    // browser doesn't fight the manual ResizeObserver compensation below.
    const scrollEl = document.querySelector('.chat-messages');
    if (scrollEl) scrollEl.style.overflowAnchor = 'none';
    setOpenPanels((prev) => {
      const next = new Set(prev);
      if (next.has(panel)) next.delete(panel); else next.add(panel);
      return next;
    });
  };

  // Immediate scroll compensation before the browser paints the new tab content.
  useLayoutEffect(() => {
    const anchor = tabChangeAnchorRef.current;
    if (!anchor || !messageContainerRef.current) return;

    const scrollEl = document.querySelector('.chat-messages');
    if (!scrollEl) return;

    const newTop = messageContainerRef.current.getBoundingClientRect().top;
    const delta = newTop - anchor.containerTop;
    if (Math.abs(delta) > 0.5) {
      scrollEl.scrollTop += delta;
      // Update anchor so the ResizeObserver (below) doesn't double-correct.
      anchor.containerTop = messageContainerRef.current.getBoundingClientRect().top;
    }
  }, [openPanels]);

  // ResizeObserver-based scroll stabilization.
  // A single tab switch can trigger MULTIPLE height changes:
  //   1. The initial content swap  (caught by useLayoutEffect above)
  //   2. Pagination state clearing / re-applying  (async useEffect)
  //   3. ServerSideDataTable hydrating its rows   (async setState)
  // The ResizeObserver fires on every height change and keeps the card
  // pinned to its original viewport position until the switch settles.
  // Cleanup is debounced: the anchor clears 150ms after the LAST resize,
  // so slow-loading content on any network is fully covered without a
  // fixed deadline that could expire too early.
  useEffect(() => {
    const el = messageContainerRef.current;
    const scrollEl = document.querySelector('.chat-messages');
    if (!el || !scrollEl) return;

    let settleTimer = null;

    const ro = new ResizeObserver(() => {
      const anchor = tabChangeAnchorRef.current;
      if (!anchor) return;

      const currentTop = el.getBoundingClientRect().top;
      const delta = currentTop - anchor.containerTop;
      if (Math.abs(delta) > 0.5) {
        scrollEl.scrollTop += delta;
        anchor.containerTop = el.getBoundingClientRect().top;
      }

      // Debounce: declare "settled" 150ms after the last resize event.
      // This adapts to how long content actually takes to load, rather
      // than an arbitrary fixed timeout.
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        tabChangeAnchorRef.current = null;
        scrollEl.style.overflowAnchor = '';
      }, 150);
    });

    ro.observe(el);
    return () => {
      ro.disconnect();
      clearTimeout(settleTimer);
    };
  }, []);

  // Fallback: if no resize fires at all (new tab has the exact same
  // height as the old one), the ResizeObserver debounce never runs.
  // Restore native scroll anchoring after a short delay — intentionally
  // longer than the 150ms debounce so this only activates in the
  // no-resize edge case.
  useEffect(() => {
    if (!tabChangeAnchorRef.current) return;
    const id = setTimeout(() => {
      tabChangeAnchorRef.current = null;
      const scrollEl = document.querySelector('.chat-messages');
      if (scrollEl) scrollEl.style.overflowAnchor = '';
    }, 300);
    return () => clearTimeout(id);
  }, [openPanels]);
  const [chartData, setChartData] = useState([]);
  const [feedback, setFeedback] = useState(initialFeedback);
  const [showModal, setShowModal] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [displayData, setDisplayData] = useState(() => {
    // For SQL and Insights datatypes, parse the message to get initial display data
    if (datatype === "sql" || datatype === "insights") {
      // Parse ALL data for client-side pagination (up to 30 rows from session API)
      return parseMessageToArray(message, null);
    }
    return Array.isArray(message) && message.length > 0 ? message.slice(0, 10) : [];
  });
  const [pageSize] = useState(10);
  const [paginationState, setPaginationState] = useState({ showPagination: false });
  const [docxPreview, setDocxPreview] = useState(null);
  const [highlightPreview, setHighlightPreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  // Which citation chunk is currently being opened. The ref guards against
  // synchronous double-clicks (state updates are async); the state drives the
  // pill's spinner/disabled UI.
  const [loadingChunkIdx, setLoadingChunkIdx] = useState(null);
  const loadingChunkRef = useRef(false);
  const isAuthorAdmin = authorId === 0;
  const isSQLDatatype = datatype === "sql";
  const isInsightsDatatype = datatype === "insights";

  // Infer answer_type from message data when not provided (e.g., loaded session history)
  const effectiveAnswerType = useMemo(() => {
    if (answer_type) return answer_type;
    // Only infer for completed bot messages (not during streaming)
    if (isStreaming || authorId !== 0) return null;
    // Has SQL queries or table data with source → sql
    if ((isSQLDatatype || isInsightsDatatype) && source) return 'sql';
    // Has curl commands → api
    if (curl_commands && curl_commands.length > 0) return 'api';
    // Has clarification options → clarification
    if (clarificationOptions && clarificationOptions.length > 0) return 'clarification';
    // Has RAG document references → rag
    if (reference?.chunks?.length > 0) return 'rag';
    // Has description/insights but no source → rag
    if (description && typeof description === 'string' && description.trim() !== '' && !source) return 'rag';
    // Insights datatype with no SQL source and no API commands → rag
    // (RAG fast mode may have empty description but still has insights datatype)
    if (isInsightsDatatype && !source && !(curl_commands && curl_commands.length > 0)) return 'rag';
    // Has SQL source or table data → sql
    if (isSQLDatatype || isInsightsDatatype) return 'sql';
    return null;
  }, [answer_type, isStreaming, authorId, isSQLDatatype, isInsightsDatatype, source, curl_commands, clarificationOptions, description, reference]);

  // Determine tab configuration from answer_type and analysis_mode
  const tabConfig = useMemo(() => {
    const hasSummary = analysis_mode === 'fast' && !!description?.trim();
    const hasInsights = !!description?.trim();
    return getTabConfig(effectiveAnswerType, analysis_mode, hasSummary, hasInsights);
  }, [effectiveAnswerType, analysis_mode, description]);
  // Removed unused hasCurlCommands
  const serviceUrl = config.serviceUrl; // Use dynamic config
  const msgClass = "p-3";

  const [costData, setCostData] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costError, setCostError] = useState(null);
  const [showCostModal, setShowCostModal] = useState(false);

  const fetchTransactionCost = async () => {
    if (!id || costLoading || !config?.apiUrl) return;
    setCostLoading(true);
    setCostError(null);
    setCostData(null);
    try {
      const apiBase = config.apiUrl.replace(/\/api\/v1\/?$/, '');
      const response = await axios.get(
        `${apiBase}/v1/observability/transaction/${id}/cost`,
        { headers: { Authorization: `Bearer ${getAccessToken()}` } }
      );
      const payload = response.data;
      if (payload?.code !== 0 || payload?.data == null) {
        setCostError(payload?.message || 'Transaction not found');
      } else {
        setCostData(payload.data);
      }
    } catch (error) {
      setCostError(error.response?.data?.message || error.message || 'Failed to fetch cost data');
    } finally {
      setCostLoading(false);
      setShowCostModal(true);
    }
  };
  
  // Infer total_count from displayData if totalRows is missing
  // This ensures consistent pagination behavior across all agents
  const effectiveTotalRows = useMemo(() => {
    if (totalRows != null) {
      return totalRows;
    }
    // If total_count is missing but we have displayData for SQL or Insights datatype,
    // infer it from displayData length (session API provides up to 30 rows)
    if ((isSQLDatatype || isInsightsDatatype) && displayData && displayData.length > 0) {
      return displayData.length;
    }
    return null;
  }, [totalRows, isSQLDatatype, isInsightsDatatype, displayData]);

  // For multi-table responses, parse each data source independently.
  // Entries with non-table content get empty arrays — MultiSourceTabs still renders
  // with the source keys so ServerSideDataTable can fetch via the pagination API.
  const perSourceData = useMemo(() => {
    if (!dataSourcesMap || typeof dataSourcesMap !== 'object') return null;
    const result = {};
    for (const [key, content] of Object.entries(dataSourcesMap)) {
      result[key] = parseMessageToArray(content, null) || [];
    }
    return Object.keys(result).length > 0 ? result : null;
  }, [dataSourcesMap]);

  // Build chunk-level citation map: chunk index → sequential citation number by first
  // appearance across message + description. Multiple chunks from the same document each
  // get their own number, so ##0$, ##2$, ##4$ from one PDF become [1], [2], [3].
  const { citationMap } = useMemo(() => {
    if (!reference?.chunks?.length) {
      return { citationMap: null, citedChunks: [] };
    }
    // Scan both message and description for ##N$ markers in order of first appearance
    const textsToScan = [message, description].filter(t => typeof t === 'string' && t);
    if (textsToScan.length === 0) {
      return { citationMap: null, citedChunks: [] };
    }
    const orderedIndices = [];
    const seen = new Set();
    const markerRe = /##(\d+)\$+/g;
    for (const text of textsToScan) {
      let m;
      markerRe.lastIndex = 0;
      while ((m = markerRe.exec(text)) !== null) {
        const idx = parseInt(m[1], 10);
        if (!seen.has(idx) && idx < reference.chunks.length) {
          seen.add(idx);
          orderedIndices.push(idx);
        }
      }
    }
    // map[chunkIdx] = 1-based citation number by order of first appearance
    const map = {};
    orderedIndices.forEach((chunkIdx, i) => { map[chunkIdx] = i + 1; });
    // ordered list of cited chunk objects for source pills
    const cited = orderedIndices.map(idx => ({ ...reference.chunks[idx], _chunkIdx: idx }));
    return { citationMap: map, citedChunks: cited };
  }, [message, description, reference]);

  // Replace ##N$$ citation markers with inline clickable [i](#cite-idx) links.
  // Using #cite-idx instead of cite:idx to guarantee it passes SAFE_HREF_RE and ReactMarkdown
  // parses it unambiguously as an inline link (cite: is non-standard and may be normalised away).
  const replaceCitationMarkers = useCallback((text) => {
    if (!citationMap || !text || typeof text !== 'string') return text;
    return text.replace(/ *(##(\d+)\$+) */g, (match, _full, numStr) => {
      const idx = parseInt(numStr, 10);
      const pad = match.startsWith(' ') ? ' ' : '';
      return citationMap[idx] !== undefined ? `${pad}[${citationMap[idx]}](#cite-${idx})` : pad;
    });
  }, [citationMap]);

  const replaceHTMLCitationMarkers = useCallback((html) => {
    if (!citationMap || !html || typeof html !== 'string') return html;
    return html.replace(/([ ]*)##(\d+)\$+([ ]*)(?=[^$]|$)/g, (_match, spaceBefore, numStr, spaceAfter) => {
      const idx = parseInt(numStr, 10);
      if (citationMap[idx] === undefined) return spaceBefore;
      const style = `display:inline-flex;align-items:center;justify-content:center;vertical-align:super;font-size:10px;font-weight:600;line-height:1;border-radius:9999px;width:16px;height:16px;min-width:16px;margin:0 2px;background-color:var(--brand-soft);color:var(--brand);cursor:pointer;text-decoration:none;`;
      return spaceBefore + `<a href="#cite-${idx}" style="${style}" title="View source">${citationMap[idx]}</a>` + spaceAfter;
    });
  }, [citationMap]);

  const rawProcessedMessage = useMemo(() => replaceCitationMarkers(message), [message, replaceCitationMarkers]);
  const rawProcessedDescription = useMemo(() => {
    if (!description || typeof description !== 'string') return description;
    if (looksLikeHTML(description)) return replaceHTMLCitationMarkers(description);
    return replaceCitationMarkers(description);
  }, [description, replaceCitationMarkers, replaceHTMLCitationMarkers]);

  // Pace streamed prose so it arrives word by word instead of in the blobs the
  // SSE chunks actually land in. Only prose: a half-arrived markdown table, HTML
  // fragment or JSON payload renders as garbage, so those pass through whole.
  const isPaceableProse = useCallback((text) => {
    if (typeof text !== 'string' || !text) return false;
    if (looksLikeHTML(text) || isJSONResponse(text)) return false;
    // A markdown table mid-parse renders as broken pipes rather than rows.
    if (/^\s*\|/m.test(text)) return false;
    return true;
  }, []);

  // The card mounts as soon as answer_type lands — usually while the answer is
  // still arriving — so the wrapper's entrance animation has already played by
  // the time there is anything to read. This replays it on the body at the
  // moment the turn completes, which is when a reader actually looks. Adding
  // the class (rather than remounting) keeps table and tab state intact.
  // What the reader sees first, and what waits underneath it. When the backend
  // gives us a written answer that is the primary panel; when it does not (a
  // pure table reply) the table itself is the answer and leads.
  // defaultTab is the field that actually reflects content — deep mode lists
  // every tab in visibleTabs whether or not insights exist, so reading
  // visibility here put "No description available" where the answer goes and
  // left the table collapsed behind a chip.
  // `tabConfig` reads this off `answer_type`, which a reopened session's payload
  // does not carry — so a reloaded turn with a perfectly good written answer
  // looked like a table-only one and rendered a result card in place of its
  // prose. The content itself is the honest signal, and `defaultTab` stays
  // authoritative when it has an opinion.
  const hasProse = typeof description === 'string' && description.trim() !== '';
  const hasWrittenAnswer = tabConfig.defaultTab === 'description' || hasProse;
  // Always the written answer now. Rows live in the data rail, so a turn with
  // nothing written does not lead with its table — it leads with a card that
  // says what the table holds (see `resultCard` below) and sends you to the rail.
  const primaryPanel = 'description';
  const answerIsProse = !tabConfig.showTabs || hasWrittenAnswer;
  // Named explicitly rather than "anything that is not data is the query".
  // A deep turn with no insights leads with the table, which left `description`
  // in the supporting list — and a catch-all mapping turned it into a second
  // chip keyed 'source', colliding with the real one and rendering the wrong
  // panel. `description` is the written answer: it either leads or, when there
  // is nothing written, has nothing to show.
  // Only panels that open in place live here. The Data chip is built inline
  // below because it is a trigger, not a panel, and giving it a row in this
  // table invited exactly the mistake of rendering it as one.
  const PANEL_CHROME = {
    source: { key: 'source', label: 'Source', Icon: Code2, meta: null },
  };
  // Panels that open in place. `data` is excluded deliberately: it is a chip that
  // opens the rail, not a panel, and the two read differently on purpose —
  // `Data ›` goes sideways, `Source ∨` expands here.
  const supportingPanels = tabConfig.showTabs
    ? tabConfig.visibleTabs
        .filter((t) => t !== primaryPanel && t !== 'data')
        .map((t) => PANEL_CHROME[t])
        .filter(Boolean)
    : [];

  // Whether this turn has rows to send to the rail, and what to say about them
  // when it has nothing else to show. One selector, shared with the rail, so the
  // chip and the panel it opens can never describe different things.
  // `datatype` already arrives resolved — ChatScreen passes `msg.datatype ||
  // msg.type` — so the selector needs no second field to disambiguate.
  const rows = useMemo(() => resultSet({
    id,
    datatype,
    message,
    total_rows: totalRows,
    data_sources_map: dataSourcesMap,
  }), [id, datatype, message, totalRows, dataSourcesMap]);
  // Driven by the rows themselves, NOT by `tabConfig`. A reopened session's
  // payload carries `answer.data_sources` and `total_count` but no
  // `answer_type`, so `getTabConfig` returns showTabs:false for it — which left
  // a reloaded turn with its rows intact and no chip to reach them.
  //
  // A turn with no written answer shows a result card instead, and the card is
  // itself the way into the rail — a chip beside it would count the same rows a
  // second time. The "no rows" chip needs the tab config, because with no rows
  // there is nothing else to say a data channel existed.
  const hasRowChannel = tabConfig.showTabs && tabConfig.visibleTabs.includes('data');
  const showDataChip = rows ? hasWrittenAnswer : hasRowChannel;
  const rowChipCount = rows
    ? (typeof rows.totalRows === 'number'
        ? rows.totalRows
        : rows.outputs.reduce((sum, output) => sum + (output.totalRows || 0), 0))
    : 0;

  const [answerLanded, setAnswerLanded] = useState(false);
  const wasStreamingRef = useRef(isStreaming);
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) {
      setAnswerLanded(true);
      animTrace('turn finished -> replay entrance', { id });
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, id]);

  // Traced only with debugChatAnim set; see utils/animDebug.
  useEffect(() => {
    animTraceEnvironment();
    animTrace('message mounted', { id, isStreaming, answer_type, datatype });
    return () => animTrace('message unmounted', { id });
    // Mount/unmount only: remounting mid-turn is itself one of the things worth
    // seeing, and it would be hidden by re-running this on every change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The two fields carry different things and pace differently. On a sql or
  // insights answer `message` is row data that gets parsed into a table, so it
  // has to arrive whole — but `description` is the written answer on those same
  // turns, and it is the thing the reader is actually waiting on.
  const processedMessage = useSmoothReveal(rawProcessedMessage, isStreaming, {
    enabled: isAuthorAdmin && !isSQLDatatype && !isInsightsDatatype
      && isPaceableProse(rawProcessedMessage),
  });
  const paceDescription = isAuthorAdmin && isPaceableProse(rawProcessedDescription);
  const processedDescription = useSmoothReveal(rawProcessedDescription, isStreaming, {
    enabled: paceDescription,
  });

  animTrace('render', {
    id,
    isStreaming,
    answer_type: effectiveAnswerType,
    showTabs: tabConfig.showTabs,
    paceDescription,
    descriptionChars: typeof rawProcessedDescription === 'string' ? rawProcessedDescription.length : null,
    shownChars: typeof processedDescription === 'string' ? processedDescription.length : null,
  });

  const handleSourceClick = useCallback(async (chunkIdx) => {
    // Ignore clicks while another citation is already opening so repeated
    // clicks don't fire duplicate signed-URL requests / open stacked modals.
    if (loadingChunkRef.current) return;
    const chunk = reference?.chunks?.[chunkIdx];
    if (!chunk) return;
    const docId = chunk.document_id || chunk.doc_id;
    if (!docId) return;
    loadingChunkRef.current = true;
    setLoadingChunkIdx(chunkIdx);
    try {
    let baseUrl = '';
    try { baseUrl = new URL(config.apiUrl).origin; } catch { /* keep empty */ }

    // `#page=N` jumps PDF viewers to the cited page. When the cited chunk
    // itself has no positions (content-free nav/heading chunk), fall back to
    // the first page from any sibling chunk of the same document.
    let pageNum = chunk.positions?.[0]?.[0];
    if (!pageNum) {
      const sibling = reference?.chunks?.find(
        (c) => (c?.document_id || c?.doc_id) === docId && c?.positions?.[0]?.[0]
      );
      pageNum = sibling?.positions?.[0]?.[0];
    }
    const fragment = pageNum ? `#page=${pageNum}` : '';

    const VIDEO_EXTS = ['mp4', 'webm', 'ogg', 'ogv', 'm4v', 'mov'];
    const firstPos = chunk.positions?.find((p) => Array.isArray(p) && p.length >= 5 && p[0]);
    const hasBbox = firstPos && (firstPos[1] || firstPos[2] || firstPos[3] || firstPos[4]);

    const accessToken = getAccessTokenRef.current();
    const authHeader = accessToken ? `Bearer ${accessToken}` : '';

    // Resolve a direct-fetch URL via the signed-URL endpoint so the file is
    // loaded from the user's own storage (S3 / GCS / MinIO) instead of being
    // streamed through Discover. Falls back to the proxy URL for providers
    // that don't expose signed URLs (Google Drive, GCS user-OAuth).
    const proxyUrl = `${baseUrl}/v1/document/get/${docId}`;
    let resolved = { url: proxyUrl, source: 'proxy', provider: 'unknown' };
    try {
      const res = await fetch(`${baseUrl}/v1/document/signed_url/${docId}`, {
        credentials: 'include',
        headers: authHeader ? { Authorization: authHeader } : undefined,
      });
      if (res.ok) {
        const payload = await res.json();
        // /signed_url returns { code, data: { url, source, provider, expires_in } }
        const data = payload?.data || payload;
        if (data?.url) resolved = data;
      }
    } catch {
      // Network/JSON error — fall back to proxy URL.
    }

    // Knowledge-graph chunks (entities/relationships) carry a real
    // `document_id` but an empty `document_name`, so we can't tell the file
    // type up front. Recover both the display name and the extension from the
    // resolved URL's path — the signed/proxy URL ends in the real filename.
    let docName = chunk.document_name || chunk.docnm_kwd || '';
    let ext = docName.split('.').pop()?.toLowerCase() || '';
    if (!docName || !ext || ext === docName.toLowerCase()) {
      try {
        const path = new URL(resolved.url, baseUrl || window.location.origin).pathname;
        const base = decodeURIComponent(path.split('/').pop() || '');
        const urlExt = urlFileExt(base);
        if (!docName && base) docName = base;
        if (urlExt) ext = urlExt;
      } catch { /* keep what we have */ }
    }
    if (!docName) docName = 'Document';
    const isVideo = VIDEO_EXTS.includes(ext);

    // The "Open full ..." CTA navigates a new tab straight to this URL, which
    // can't carry a bearer header. So it only works for real cloud/signed URLs;
    // when the signed-URL lookup fell back to the auth proxy there is no
    // externally-openable URL and the CTA must be hidden (otherwise the new tab
    // shows the backend's "Authentication required" error).
    const externalUrl = resolved.source === 'proxy' ? null : resolved.url;

    // Highlight-preview modal: only meaningful for PDFs with real bbox
    // positions. PNG is rendered server-side from the cited page; the full
    // doc opens via the resolved (cloud) URL when the user clicks the CTA.
    // PDF modal: render the whole document inline via the backend proxy and
    // highlight the cited chunk — using its real bbox coordinates when present,
    // otherwise aligning the chunk text against the rendered text layer. Same
    // privacy model as the DOCX proxy (bytes streamed in-memory). The "Open
    // full document" link still points at the cloud URL via the user's session.
    if (ext === 'pdf') {
      setPdfPreview({
        docName,
        fetchUrl: `${proxyUrl}?stream=1`,
        downloadUrl: `${proxyUrl}?stream=1`,
        fullDocUrl: externalUrl ? externalUrl + fragment : null,
        source: resolved.source,
        provider: resolved.provider,
        chunkContent: chunk.content || '',
        chunkPositions: chunk.positions || [],
        hasBbox: Boolean(hasBbox),
        authHeader,
      });
      return;
    }

    // DOCX modal: always rendered via the backend proxy (uses stored connector
    // token, in-memory only — same privacy model as our Drive/GCS PDF
    // proxy). The "Open full document" CTA still points at the cloud URL so
    // the user can hit storage.cloud.google.com / drive.google.com / S3
    // signed URL via their own browser session.
    if (ext === 'docx') {
      // ?stream=1 forces the backend to proxy the bytes instead of 302-ing to
      // the cloud URL (which docx-preview can't fetch because of CORS on
      // storage.cloud.google.com / drive.google.com).
      setDocxPreview({
        docName,
        fetchUrl: `${proxyUrl}?stream=1`,
        downloadUrl: `${proxyUrl}?stream=1`,
        fullDocUrl: externalUrl ? externalUrl + fragment : null,
        source: resolved.source,
        provider: resolved.provider,
        chunkContent: chunk.content || '',
        authHeader,
      });
      return;
    }

    // Video modal: play the resolved URL inline with a <video> element. Signed
    // cloud URLs stream cross-origin without CORS; the same-origin proxy URL
    // sends cookies automatically. Download/Open CTAs cover the bearer-auth
    // case the media element can't handle.
    if (isVideo) {
      setVideoPreview({
        docName,
        videoUrl: resolved.url,
        downloadUrl: `${proxyUrl}?stream=1`,
        fullDocUrl: externalUrl,
        source: resolved.source,
        provider: resolved.provider,
        authHeader,
      });
      return;
    }

    // Generic fallback: no purpose-built preview applies (PDF without bbox,
    // unknown/extension-less type, etc.). Still open a modal "no matter what"
    // by framing the resolved document URL — browsers render PDFs and images
    // inline; framing-blocked providers fall back to the modal's "Open in new
    // tab" CTA. `#page=N` is honoured by the embedded PDF viewer.
    setFilePreview({
      docName,
      previewUrl: resolved.url + fragment,
      downloadUrl: `${proxyUrl}?stream=1`,
      fullDocUrl: externalUrl ? externalUrl + fragment : null,
      source: resolved.source,
      provider: resolved.provider,
      authHeader,
      ext,
    });
    } finally {
      // Cleared once the preview state is set (modals open synchronously after
      // this); re-enables the pills for the next citation.
      loadingChunkRef.current = false;
      setLoadingChunkIdx(null);
    }
  // getAccessToken is intentionally absent from these deps: it gets a new identity
  // on every AuthContext render, so including it would rebuild this callback
  // constantly. The token is read through getAccessTokenRef instead, which the
  // effect above keeps current — so this stays memoized AND cannot serve a token
  // from a previous render. (An earlier version of this comment claimed the bare
  // getAccessToken() call was already "read fresh"; it was not. A closure captures
  // the accessor, so citations would 401 after a silent renewal until `reference`
  // happened to change.)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, config.apiUrl]);

  const token = localStorage.getItem("token");

  // Initialize displayData when message changes
  // For SQL and Insights datatypes, parse ALL data for client-side pagination (session API provides up to 30 rows)
  useEffect(() => {
    if (isSQLDatatype || isInsightsDatatype) {
      const parsed = parseMessageToArray(message, null) || [];
      if (parsed.length > 0) {
        // Always update when there is real data
        setDisplayData(parsed);
      } else if (!isStreaming) {
        // Only clear to empty after streaming completes — avoids flicker when
        // an intermediate non-table chunk temporarily sets message to ""
        setDisplayData(parsed);
      }
      // If streaming and parsed is empty, keep the existing displayData so the
      // Data tab continues showing the last known records during the transition.
    }
  }, [message, isSQLDatatype, isInsightsDatatype, isStreaming]);

  // For non-SQL/non-Insights datatypes, limit displayed data based on pageSize
  useEffect(() => {
    if (!isSQLDatatype && !isInsightsDatatype && Array.isArray(message) && message.length > 0) {
      setDisplayData(message.slice(0, pageSize));
    }
  }, [message, pageSize, isSQLDatatype, isInsightsDatatype]);

  // Update feedback state when initialFeedback prop changes (e.g., when loading a session)
  useEffect(() => {
    setFeedback(initialFeedback);
  }, [initialFeedback, id]);

  const handleClarificationOptionClick = (option) => {
    if (onClarificationOptionClick) {
      onClarificationOptionClick(option, id);
    }
  };

  // Utility functions
  const isTable = () => {
    try {
      if (typeof message === "string" && message.trim().startsWith("|")) {
        const firstLine = message.trim().split("\n")[0];
        return firstLine.split("|").length > 2;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  const containsMarkdown = () => {
    if (typeof message !== "string") return false;
    // Citation markers (##N$$) will be converted to markdown links in processedMessage
    if (/##\d+\$+/.test(message)) return true;
    const markdownPatterns = [
      /^#+\s/,
      /\*\*.+\*\*/,
      /_.+_/,
      /`[^`]+`/,
      /```[\s\S]+```/,
      /^\s*[-*+]\s/,
      /^\s*\d+\.\s/,
      /\[.+\]\(.+\)/,
      /!\[.+\]\(.+\)/,
      /\|.*\|.*\|/,
      /^>\s/,
    ];
    return markdownPatterns.some((pattern) => pattern.test(message));
  };

  const sendMessageFeedback = async (sessionId, messageId, feedback, feedbackMessage = "") => {
    try {
      const response = await axios.put(
        `${config.apiUrl}/agents/${config.agentId}/messages/feedback`,
        { session_id: sessionId, message_id: messageId, feedback: feedback, feedback_message: feedbackMessage },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${getAccessToken()}` } }
      );
      return response.data;
    } catch (error) {
      console.error("Error sending feedback:", error);
      console.error("Error details:", error.response?.data);
      return null;
    }
  };

  const handleFeedback = async (feedbackValue, comment = "") => {
    try {
      const newFeedback = feedback === feedbackValue ? 0 : feedbackValue;
      
      // Send the NEW feedback value to the API (not the clicked value)
      const response = await sendMessageFeedback(chatSessionId, id, newFeedback, comment || "");
      if (response) {
        setFeedback(newFeedback);
        
        // Save to localStorage as backup (until backend returns feedback in API)
        try {
          const FEEDBACK_STORAGE_KEY = 'message_feedback_cache';
          const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
          const feedbackCache = stored ? JSON.parse(stored) : {};
          if (newFeedback === 0) {
            delete feedbackCache[id];
          } else {
            feedbackCache[id] = newFeedback;
          }
          localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(feedbackCache));
        } catch (storageError) {
          console.error('Error saving to localStorage:', storageError);
        }
        
        // Update the liveChat state to persist feedback in current session
        setLiveChat((prevMessages) => 
          prevMessages.map((msg) => 
            msg.id === id ? { ...msg, feedback: newFeedback } : msg
          )
        );
      } else {
        console.error(`Failed to save feedback for message ${id}`);
      }
    } catch (error) {
      console.error("Error handling feedback:", error);
    }
  };

  const onReply = async () => {
    // Convert source or message to a displayable string
    let quotedContent = isSQLDatatype ? source : message;
    
    // Handle arrays of objects (like SQL queries with component_id and query)
    if (Array.isArray(quotedContent)) {
      quotedContent = quotedContent.map((item) => {
        if (typeof item === "object" && item !== null) {
          return item.query || JSON.stringify(item);
        }
        return String(item);
      }).join("\n");
    } else if (typeof quotedContent === "object" && quotedContent !== null) {
      quotedContent = quotedContent.query || JSON.stringify(quotedContent);
    }
    
    setQuotedMessage(quotedContent);
    setCurrentParentId(id);
    await resizeInput();
  };

  const handleSend = async (response) => {
    const tempChatId = id + Date.now().toString();
    const requestBody = {
      id: tempChatId,
      message: response,
      chat_session_id: chatSessionId,
      parent_message_id: setCurrentParentId,
      author_id: authorId,
    };
    setLiveChat((prevMessage) => [...prevMessage, requestBody]);
    setQuotedMessage(null);
    setCurrentParentId(null);
    resizeInput();

    const config_home = {
      headers: { "Content-Type": "application/json", Authorization: token },
    };

    try {
      const response = await axios.post(serviceUrl, requestBody, config_home);
      setLiveChat((prevMessages) => {
        const updatedMessages = [...prevMessages];
        const lastMessageIndex = updatedMessages.length - 1;
        updatedMessages[lastMessageIndex].id = response.data.user_message_id;
        return updatedMessages;
      });
      chatSessionId = response.data.chat_session_id;
      setLiveChat((prevMessage) => [...prevMessage, response.data.bot_message]);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleDislikeClick = () => {
    // If already disliked, toggle back to neutral
    if (feedback === -1) {
      handleFeedback(-1, ""); // This will toggle to 0 (neutral)
    } else {
      // Not disliked yet, show modal for comment
      setShowModal(true);
    }
  };

  const handleModalSubmit = async () => {
    if (feedbackComment.trim().length < 1 || isSubmittingFeedback) return;
    
    setIsSubmittingFeedback(true);
    try {
      await handleFeedback(-1, feedbackComment);
      setFeedbackComment("");
      setShowModal(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleModalClose = () => {
    // Close without saving feedback
    setFeedbackComment("");
    setShowModal(false);
    setIsSubmittingFeedback(false);
  };

  // Helper function to safely render parent message text
  const getParentMessageDisplay = () => {
    if (!parentMessage) return null;
    
    const text = parentMessage.type === "sql" 
      ? parentMessage.source 
      : parentMessage.message;
    
    // Handle different types of content
    if (typeof text === "string") {
      return text;
    } else if (Array.isArray(text)) {
      return text.map((item) => {
        // If item is an object with a query property, extract it
        if (typeof item === "object" && item !== null) {
          if (item.query) {
            return item.query;
          }
          // Otherwise stringify the object
          return JSON.stringify(item);
        }
        return String(item);
      }).join("\n---\n");
    } else if (typeof text === "object" && text !== null) {
      // If it's a single object with query property
      if (text.query) {
        return text.query;
      }
      return JSON.stringify(text, null, 2);
    }
    
    return String(text || "");
  };

  const parentMessageText = getParentMessageDisplay();

  return (
    <CitationLoadingContext.Provider value={loadingChunkIdx}>
    <WebSourcesContext.Provider value={reference?.web_sources ?? null}>
    {/* Both sides of the conversation share one centred column — `chat-column`,
        the same class the live timeline and the composer use. Pinning the answer
        left and the question right is what made a turn span the window: the eye
        crossed about 1590px between one and the next, and neither shared an edge
        with the composer. The question still right-aligns, but INSIDE the
        column, so the two sit on the same axis. */}
    <div className={`${msgClass} w-full flex justify-center`}>
      <div className={`chat-column flex ${isAuthorAdmin ? 'items-start gap-3' : 'flex-col items-end'}`}>
        {!isAuthorAdmin && (
          <div className="flex max-w-[85%] items-start gap-3 animate-rise">
            <div>
              {parentMessageText && (
                <div className="mb-3 p-3 rounded-lg border-l-4 bg-surface-raised border-brand">
                  <p className="text-xs font-semibold mb-1 text-brand">
                    Replying To:
                  </p>
                  <p className="text-sm line-clamp-2 whitespace-pre-wrap text-fg-secondary">
                    {parentMessageText}
                  </p>
                </div>
              )}
              {/* The question is context, not the headline — a tinted bubble
                  reads as "you said this" without out-shouting the answer it
                  sits above. The flattened corner points back at the sender. */}
              <div className="rounded-2xl rounded-br-md px-4 py-3 bg-brand-soft border border-line text-fg">
                <div className="text-sm leading-relaxed break-words whitespace-pre-wrap">
                  {isJSONResponse(message) ? (
                    <div className="markdown-content break-words">
                      <MarkdownRenderer content={parseJSONResponse(message)} />
                    </div>
                  ) : typeof message === "object" ? (
                    JSON.stringify(message)
                  ) : (
                    /* Same chip the composer paints while the question is being
                       typed — the mention survives the send instead of flattening
                       back into plain text the moment it lands in the transcript. */
                    <MentionText text={message} />
                  )}
                </div>
              </div>
              {/* Timestamp for user messages */}
              <DynamicTimestamp
                timestamp={timestamp}
                isUserMessage={true}
              />
            </div>
            <div className="flex-shrink-0">
              <UserAvatar
                photoURL={avatar}
                userName={author}
              />
            </div>
          </div>
        )}
        {isAuthorAdmin && (
          /* No avatar on the assistant side: one speaker per side already says
             who is talking, and a lettered gradient circle on every turn is
             chrome the answer has to compete with. */
          <div className="flex-1 min-w-0 group/msg animate-rise">
          {parentMessageText && (
            <div className="mb-3 p-3 rounded-lg border-l-4 bg-surface-raised border-brand">
              <p className="text-xs font-semibold mb-1 text-brand">
                Replying To:
              </p>
              <p className="text-sm line-clamp-2 whitespace-pre-wrap text-fg-secondary">
                {parentMessageText}
              </p>
            </div>
          )}

          <ActivityTimeline steps={reasoning_steps} isStreaming={isStreaming} isDarkMode={isDarkMode} />

          {/* One shape for every answer. The written reply always sits directly
              on the page; the query that produced it opens underneath, and the
              rows open beside it. */}
          <div ref={messageContainerRef}>
            {/* A turn whose whole answer WAS its table would otherwise leave
                nothing here at all, now that rows render in the rail. It gets a
                card that says what the result holds — the columns and the count,
                which is what a reader needs to decide whether to open it. */}
            {!hasWrittenAnswer && rows && (
              <button
                type="button"
                onClick={() => onOpenData?.(id)}
                className="flex w-full items-center justify-between gap-4 rounded-xl border border-line bg-surface px-4 py-3 text-left transition-colors hover:border-brand"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-fg">
                    {/* The chip's own total, so the card and the chip can never
                        disagree — the first output's count under a label saying
                        "in N outputs" undercounted every multi-output turn. */}
                    {(rowChipCount || 0).toLocaleString()}{' '}
                    {rowChipCount === 1 ? 'row' : 'rows'}
                    {rows.isMultiSource ? ` in ${rows.outputs.length} outputs` : ''}
                  </span>
                  <span className="mt-0.5 block truncate font-mono text-xs text-fg-muted">
                    {rows.outputs[0].columns.join(', ') || 'No records to display'}
                  </span>
                </span>
                <span className="shrink-0 text-xs font-semibold text-brand">Open ›</span>
              </button>
            )}
            <div className={`overflow-x-auto text-fg${answerIsProse ? " max-w-[72ch]" : ""}${answerLanded ? " animate-rise" : ""}`}>
              {tabConfig.showTabs ? (
                <TabContent
                  selectedTab={primaryPanel}

                    id={id}
                    isSQLDatatype={isSQLDatatype}
                    isInsightsDatatype={isInsightsDatatype}
                    totalRows={effectiveTotalRows}
                    displayData={displayData}
                    message={processedMessage}
                    isTable={isTable}
                    containsMarkdown={containsMarkdown}
                    handleSend={handleSend}
                    isAuthorAdmin={isAuthorAdmin}
                    clarificationOptions={clarificationOptions}
                    handleClarificationOptionClick={handleClarificationOptionClick}
                    clarificationOptionsDisabled={clarificationOptionsDisabled}
                    selectedClarificationOption={selectedClarificationOption}
                    chartData={chartData}
                    setChartData={setChartData}
                    source={source}
                    datatype={datatype}
                    description={processedDescription}
                    curl_commands={curl_commands}
                    setPaginationState={setPaginationState}
                    sessionId={chatSessionId}
                    isStreaming={isStreaming}
                    perSourceData={perSourceData}
                    effectiveTotalRows={effectiveTotalRows}
                    onChunkClick={handleSourceClick}
                  />
              ) : (
                <DirectContent
                    answer_type={effectiveAnswerType}
                    analysisMode={analysis_mode}
                    message={processedMessage}
                    description={processedDescription}
                    dataSourcesMap={dataSourcesMap}
                    railOwnsRows={!!rows}
                    replaceCitationMarkers={replaceCitationMarkers}
                    isTable={isTable}
                    containsMarkdown={containsMarkdown}
                    id={id}
                    handleSend={handleSend}
                    isAuthorAdmin={isAuthorAdmin}
                    clarificationOptions={clarificationOptions}
                    handleClarificationOptionClick={handleClarificationOptionClick}
                    clarificationOptionsDisabled={clarificationOptionsDisabled}
                    selectedClarificationOption={selectedClarificationOption}
                    setPaginationState={setPaginationState}
                    isStreaming={isStreaming}
                    onChunkClick={handleSourceClick}
                  />
              )}
              <WebGroundingPanel
                searchEntryPointHtml={reference?.search_entry_point?.rendered_content}
              />
            </div>

            {(showDataChip || supportingPanels.length > 0) && (
              <div className="mt-3">
                {/* A row of chips, the way sources read under an answer —
                    what produced this, available without displacing it. */}
                <div className="flex flex-wrap gap-2">
                  {/* Rows open beside the answer rather than under it, so this
                      chip points sideways and reports its state as pressed
                      rather than expanded — there is nothing here to expand. */}
                  {showDataChip && (
                    <button
                      type="button"
                      aria-pressed={!!isDataOpen}
                      // Reads the same selector the rail renders from. Enabled
                      // off `tabConfig` alone — which knows the turn's
                      // answer_type and nothing about its payload — is how this
                      // chip came to be pressable with nothing behind it.
                      disabled={!rows}
                      title={rows ? undefined : 'This turn returned no rows'}
                      onClick={() => onOpenData?.(id)}
                      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                        !rows
                          ? 'cursor-not-allowed border-line bg-surface text-fg-muted opacity-60'
                          : isDataOpen
                            ? 'border-brand bg-brand-soft text-brand'
                            : 'border-line bg-surface text-fg-secondary hover:border-brand hover:text-brand'
                      }`}
                      style={{ transition: 'color var(--d-base) var(--ease-out), border-color var(--d-base) var(--ease-out), background-color var(--d-base) var(--ease-out)' }}
                    >
                      <Table size={13} aria-hidden="true" />
                      Data
                      {/* Silent about the count when the rows have yet to be
                          asked for — the number comes back with them. */}
                      {(!rows || !rows.needsFetch) && (
                        <span className={`font-mono font-medium ${isDataOpen ? 'text-brand' : 'text-fg-muted'}`}>
                          {rows ? `${(rowChipCount || 0).toLocaleString()} rows` : 'no rows'}
                        </span>
                      )}
                      {rows && <ChevronDown size={13} aria-hidden="true" className="-rotate-90" />}
                    </button>
                  )}
                  {supportingPanels.map((panel) => {
                    const isOpen = openPanels.has(panel.key);
                    return (
                      <button
                        key={panel.key}
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() => togglePanel(panel.key)}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                          isOpen
                            ? 'border-brand bg-brand-soft text-brand'
                            : 'border-line bg-surface text-fg-secondary hover:border-brand hover:text-brand'
                        }`}
                        style={{ transition: 'color var(--d-base) var(--ease-out), border-color var(--d-base) var(--ease-out), background-color var(--d-base) var(--ease-out)' }}
                      >
                        <panel.Icon size={13} aria-hidden="true" />
                        {panel.label}
                        {panel.meta && (
                          <span className={`font-mono font-medium ${isOpen ? 'text-brand' : 'text-fg-muted'}`}>
                            {panel.meta}
                          </span>
                        )}
                        <ChevronDown
                          size={13}
                          aria-hidden="true"
                          className={isOpen ? '' : '-rotate-90'}
                          style={{ transition: 'transform var(--d-base) var(--ease-out)' }}
                        />
                      </button>
                    );
                  })}
                </div>

                {supportingPanels.filter((panel) => openPanels.has(panel.key)).map((panel) => (
                  <div
                    key={panel.key}
                    className="mt-2 overflow-x-auto rounded-xl border border-line bg-surface p-3 text-fg animate-rise"
                  >
                    <div className="max-h-[70vh] overflow-y-auto">
                      <TabContent
                        selectedTab={panel.key}


                    id={id}
                    isSQLDatatype={isSQLDatatype}
                    isInsightsDatatype={isInsightsDatatype}
                    totalRows={effectiveTotalRows}
                    displayData={displayData}
                    message={processedMessage}
                    isTable={isTable}
                    containsMarkdown={containsMarkdown}
                    handleSend={handleSend}
                    isAuthorAdmin={isAuthorAdmin}
                    clarificationOptions={clarificationOptions}
                    handleClarificationOptionClick={handleClarificationOptionClick}
                    clarificationOptionsDisabled={clarificationOptionsDisabled}
                    selectedClarificationOption={selectedClarificationOption}
                    chartData={chartData}
                    setChartData={setChartData}
                    source={source}
                    datatype={datatype}
                    description={processedDescription}
                    curl_commands={curl_commands}
                    setPaginationState={setPaginationState}
                    sessionId={chatSessionId}
                    isStreaming={isStreaming}
                    perSourceData={perSourceData}
                    effectiveTotalRows={effectiveTotalRows}
                    onChunkClick={handleSourceClick}
                  />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Under a prose answer the toolbar waits for intent — hover, or
                keyboard focus reaching one of its buttons. It stays put whenever
                it is carrying something the reader needs unprompted: the tabbed
                results card, or page controls for a long table, which a bare
                answer can still produce. */}
            <div className={paginationState?.showPagination ? "" : REVEAL_ON_INTENT}>
            <ActionButtons
              bare
              selectedTab={primaryPanel}
              copyTab={primaryPanel}
              isSQLDatatype={isSQLDatatype}
              source={source}
              message={message}
              description={description}
              question={question || parentMessageText}
              timestamp={timestamp}
              dataSourcesMap={dataSourcesMap}
              curl_commands={curl_commands}
              onReply={onReply}
              isAuthorAdmin={isAuthorAdmin}
              feedback={feedback}
              handleFeedback={handleFeedback}
              handleDislikeClick={handleDislikeClick}
              id={id}
              chatSessionId={chatSessionId}
              paginationState={paginationState}
              isStreaming={isStreaming}
              onShowCost={fetchTransactionCost}
              costLoading={costLoading}
            />
            </div>
          </div>
          {/* Timestamp rides with the toolbar: present when asked for, out of
              the way otherwise. */}
          <div className={REVEAL_ON_INTENT}>
            <DynamicTimestamp
              timestamp={timestamp}
              isUserMessage={false}
            />
          </div>
        </div>
        )}
      </div>

      <FeedbackModal
        showModal={showModal}
        feedbackComment={feedbackComment}
        setFeedbackComment={setFeedbackComment}
        handleModalSubmit={handleModalSubmit}
        handleModalClose={handleModalClose}
        isSubmitting={isSubmittingFeedback}
      />
      {showCostModal && (
        <CostModal
          costData={costData}
          costError={costError}
          isDarkMode={isDarkMode}
          onClose={() => setShowCostModal(false)}
        />
      )}
      {docxPreview && (
        <DocxPreviewModal
          docName={docxPreview.docName}
          fetchUrl={docxPreview.fetchUrl}
          downloadUrl={docxPreview.downloadUrl}
          fullDocUrl={docxPreview.fullDocUrl}
          source={docxPreview.source}
          provider={docxPreview.provider}
          chunkContent={docxPreview.chunkContent}
          authHeader={docxPreview.authHeader}
          onClose={() => setDocxPreview(null)}
        />
      )}
      {pdfPreview && (
        <PdfPreviewModal
          docName={pdfPreview.docName}
          fetchUrl={pdfPreview.fetchUrl}
          downloadUrl={pdfPreview.downloadUrl}
          fullDocUrl={pdfPreview.fullDocUrl}
          source={pdfPreview.source}
          provider={pdfPreview.provider}
          chunkContent={pdfPreview.chunkContent}
          chunkPositions={pdfPreview.chunkPositions}
          hasBbox={pdfPreview.hasBbox}
          authHeader={pdfPreview.authHeader}
          onClose={() => setPdfPreview(null)}
        />
      )}
      {highlightPreview && (
        <HighlightPreviewModal
          docName={highlightPreview.docName}
          highlightUrl={highlightPreview.highlightUrl}
          fullDocUrl={highlightPreview.fullDocUrl}
          proxyUrl={highlightPreview.proxyUrl}
          source={highlightPreview.source}
          provider={highlightPreview.provider}
          authHeader={highlightPreview.authHeader}
          onClose={() => setHighlightPreview(null)}
        />
      )}
      {videoPreview && (
        <VideoPreviewModal
          docName={videoPreview.docName}
          videoUrl={videoPreview.videoUrl}
          downloadUrl={videoPreview.downloadUrl}
          fullDocUrl={videoPreview.fullDocUrl}
          source={videoPreview.source}
          provider={videoPreview.provider}
          startTime={videoPreview.startTime}
          authHeader={videoPreview.authHeader}
          onClose={() => setVideoPreview(null)}
        />
      )}
      {filePreview && (
        <FilePreviewModal
          docName={filePreview.docName}
          previewUrl={filePreview.previewUrl}
          downloadUrl={filePreview.downloadUrl}
          fullDocUrl={filePreview.fullDocUrl}
          source={filePreview.source}
          provider={filePreview.provider}
          authHeader={filePreview.authHeader}
          ext={filePreview.ext}
          onClose={() => setFilePreview(null)}
        />
      )}
    </div>
    </WebSourcesContext.Provider>
    </CitationLoadingContext.Provider>
  );
};

ChatMessage.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  authorId: PropTypes.number.isRequired,
  author: PropTypes.string.isRequired,
  avatar: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]).isRequired,
  source: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  description: PropTypes.string,
  totalRows: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  dataSourcesMap: PropTypes.object,
  datatype: PropTypes.string,
  parentMessage: PropTypes.shape({
    type: PropTypes.string,
    source: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  }),
  resizeInput: PropTypes.func.isRequired,
  setQuotedMessage: PropTypes.func.isRequired,
  setCurrentParentId: PropTypes.func.isRequired,
  chatSessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setLiveChat: PropTypes.func.isRequired,
  clarificationOptions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  onClarificationOptionClick: PropTypes.func,
  clarificationOptionsDisabled: PropTypes.bool,
  selectedClarificationOption: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  curl_commands: PropTypes.arrayOf(
    PropTypes.shape({
      component_id: PropTypes.string,
      curl: PropTypes.string,
      url: PropTypes.string
    })
  ),
  initialFeedback: PropTypes.number,
  timestamp: PropTypes.number,
  isStreaming: PropTypes.bool,
  analysis_mode: PropTypes.oneOf(["deep", "fast"]),
  reference: PropTypes.shape({
    chunks: PropTypes.arrayOf(PropTypes.object),
    doc_aggs: PropTypes.arrayOf(PropTypes.object),
    // Populated when Generate had google_search grounding enabled.
    // See https://ai.google.dev/gemini-api/docs/google-search
    web_sources: PropTypes.arrayOf(PropTypes.shape({
      index: PropTypes.number,
      uri: PropTypes.string,
      title: PropTypes.string,
    })),
    web_search_queries: PropTypes.arrayOf(PropTypes.string),
    search_entry_point: PropTypes.shape({
      rendered_content: PropTypes.string,
    }),
  }),
  answer_type: PropTypes.string,
  reasoning_steps: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.oneOf(['thought', 'tool', 'status']),
    content: PropTypes.string,
    tool: PropTypes.string,
    status: PropTypes.string,
    ts: PropTypes.number,
  })),
};

ChatMessage.defaultProps = {
  source: null,
  description: null,
  totalRows: null,
  datatype: null,
  parentMessage: null,
  clarificationOptions: [],
  onClarificationOptionClick: null,
  clarificationOptionsDisabled: false,
  selectedClarificationOption: null,
  curl_commands: [],
  initialFeedback: 0, // Add initialFeedback default prop
};

export default ChatMessage;